import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  ChevronsUpDown,
  Copy,
  Eye,
  Loader2,
  Plus,
  Printer,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { coerceFiniteNumber, parseFiniteNumber } from '@/lib/number';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import {
  computeBlockPoints,
  generateMonthlyOpsJobs,
  normalizeStructuredTasks,
  type OpsGeneratedJob,
  type OpsSite as GeneratedOpsSite,
  type OpsWorkBlock as GeneratedOpsWorkBlock,
} from '@/features/ops';
import { getIsoWeekNumber, getIsoWeekday, getMonthDates, parseIsoDate } from '@/features/ops/calendar';
import { AvvikelsePills, FLAG_META, orderFlags } from '@/features/avvikelser';
import { fetchSiteAvvikelser, type PortfolioFlag } from '@/features/dashboard/api';
import type { GenerationRun, Job, JobBlock, Json, Site, WorkBlock } from '@/types/database';
import { type Language } from '@/types';
import {
  allMonthValues,
  allWeekdayValues,
  cadenceOptions,
  formatMonthLabel,
  formatWeekdayShortLabel,
  modelTypeOptions,
  monthOptions,
  parityOptions,
  weekInMonthOptions,
  weekdayOptions,
} from '../constants';
import {
  approveOpsJob,
  createOpsUser,
  deleteCleanerAbsence,
  deleteSitePricing,
  regenerateOpsJobs,
  listCleanerAbsences,
  listCleanerPoints,
  listOpsUsers,
  listSitePricing,
  reassignOpsJob,
  saveOpsBundle,
  saveOpsSite,
  type LedgerEntry,
  type PlanningUserRecord,
  type SitePricingPeriod,
  updateOpsUser,
  upsertCleanerAbsence,
  upsertSitePricing,
} from '../api';
import type { CleanerAbsenceRecord } from '@/features/ops/api';
import type { OpsRuleDraft } from '../types';

interface PlanningWorkspaceProps {
  language: Language;
}

interface PlanningLiveData {
  sites: Site[];
  workBlocks: WorkBlock[];
  generationRuns: GenerationRun[];
  jobs: Job[];
  jobBlocks: JobBlock[];
  syncedAt: string;
}

type PlanningDetailTab = 'general' | 'blocks' | 'extras' | 'calendar' | 'jobs' | 'pricing' | 'print';
type TeamMemberDetailTab = 'profile' | 'points' | 'activity' | 'vacations' | 'admin';

type PlanningRoute =
  | { page: 'brfs' }
  | { page: 'calendar'; calendarSite?: string; calendarCleaner?: string; calendarStatus?: string }
  | { page: 'team' }
  | { page: 'team-member'; userId: string; tab: TeamMemberDetailTab }
  | { page: 'site'; siteId: string; tab: PlanningDetailTab };

type NoticeTone = 'success' | 'error' | 'warning';

type NoticeState = {
  tone: NoticeTone;
  text: string;
};

type SiteGeneralDraft = {
  id?: string;
  name: string;
  address: string;
  postal_code: string;
  service_start_date: string;
  status: Site['status'];
  active: boolean;
  customer_number: string;
  building_count: string;
  floor_count: string;
  apartment_count: string;
  key_info: string;
  key_count: string;
  cleaning_room_instructions: string;
  internal_notes: string;
  pin_code: string;
};

type JobListBlockRow = {
  id: string;
  workBlockId: string | null;
  generationRunId: string | null;
  sourceCode: string;
  area: string;
  label: string;
  jobKind: JobBlock['job_kind'];
  status: JobBlock['status'];
  points: number;
  requiredPhotos: number;
  sortOrder: number;
};

type JobListRow = {
  id: string;
  siteId: string;
  siteName: string;
  scheduledFor: string;
  generationRunIds: string[];
  status: Job['status'];
  assignedUserId: string | null;
  totalPoints: number;
  completedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  completionNotes: string | null;
  blocks: JobListBlockRow[];
};

type CalendarItem = {
  id: string;
  siteId: string;
  siteName: string;
  status: Job['status'];
  totalPoints: number;
  blockCount: number;
  blocks: Array<{
    id: string;
    code: string;
    area: string;
    label: string;
    points: number;
  }>;
};

type PreviewSummary = {
  jobs: OpsGeneratedJob[];
  recurring: number;
  extra: number;
  total: number;
};

type UserCreateDraft = {
  email: string;
  password: string;
  full_name: string;
  role: PlanningUserRecord['role'];
  active: boolean;
};

type UserUpdateDraft = {
  full_name: string;
  role: PlanningUserRecord['role'];
  active: boolean;
};

type TeamMemberPeriodStats = {
  visits: number;
  completedVisits: number;
  approvedVisits: number;
  pendingApprovalVisits: number;
  cancelledVisits: number;
  blocks: number;
  scheduledPoints: number;
  completedPoints: number;
  approvedPoints: number;
  pendingApprovalPoints: number;
  cancelledPoints: number;
};

type TeamMemberSummary = {
  user: PlanningUserRecord;
  month: TeamMemberPeriodStats;
  lifetime: TeamMemberPeriodStats;
  siteCount: number;
  nextVisit: string | null;
  lastVisit: string | null;
};

const NEW_SITE_ID = 'new';
const ALL_SITES = '__ALL_SITES__';
const ALL_JOB_STATUSES = '__ALL_JOB_STATUSES__';
const ALL_CLEANERS = '__ALL_CLEANERS__';
const UNASSIGNED_CLEANER = '__UNASSIGNED__';
const NONE = '__NONE__';
const detailTabs: PlanningDetailTab[] = ['general', 'blocks', 'extras', 'calendar', 'jobs', 'pricing', 'print'];
const teamMemberTabs: TeamMemberDetailTab[] = ['profile', 'points', 'activity', 'vacations', 'admin'];
const EMPTY_WORK_BLOCKS: WorkBlock[] = [];

function createDraftId(prefix: string) {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2, 10);
  return `${prefix}-${random}`;
}

function parsePlanningRoute(): PlanningRoute {
  const hash = window.location.hash.replace(/^#\/?/, '');
  const [pathPart, queryPart = ''] = hash.split('?');
  const segments = pathPart.split('/').filter(Boolean);
  const query = new URLSearchParams(queryPart);
  const requestedTab = query.get('tab');
  const siteTab = detailTabs.includes(requestedTab as PlanningDetailTab)
    ? (requestedTab as PlanningDetailTab)
    : 'general';
  const teamTab = teamMemberTabs.includes(requestedTab as TeamMemberDetailTab)
    ? (requestedTab as TeamMemberDetailTab)
    : 'profile';

  if (segments[0] === 'calendar') {
    return {
      page: 'calendar',
      calendarSite: query.get('site') ?? undefined,
      calendarCleaner: query.get('cleaner') ?? undefined,
      calendarStatus: query.get('status') ?? undefined,
    };
  }

  if (segments[0] === 'team' && segments[1]) {
    return {
      page: 'team-member',
      userId: decodeURIComponent(segments[1]),
      tab: teamTab,
    };
  }

  if (segments[0] === 'team') {
    return { page: 'team' };
  }

  if (segments[0] === 'brfs' && segments[1]) {
    return {
      page: 'site',
      siteId: decodeURIComponent(segments[1]),
      tab: siteTab,
    };
  }

  return { page: 'brfs' };
}

function buildPlanningHash(route: PlanningRoute): string {
  if (route.page === 'calendar') {
    const params = new URLSearchParams();
    if (route.calendarSite) params.set('site', route.calendarSite);
    if (route.calendarCleaner) params.set('cleaner', route.calendarCleaner);
    if (route.calendarStatus) params.set('status', route.calendarStatus);
    const qs = params.toString();
    return qs ? `#/calendar?${qs}` : '#/calendar';
  }

  if (route.page === 'team') {
    return '#/team';
  }

  if (route.page === 'team-member') {
    return `#/team/${encodeURIComponent(route.userId)}?tab=${route.tab}`;
  }

  if (route.page === 'site') {
    return `#/brfs/${encodeURIComponent(route.siteId)}?tab=${route.tab}`;
  }

  return '#/brfs';
}

function navigateToRoute(route: PlanningRoute) {
  window.location.hash = buildPlanningHash(route);
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'nyss';
  if (min < 60) return `${min} min sedan`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} tim sedan`;
  const d = Math.floor(h / 24);
  return `${d} dagar sedan`;
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const maybeMessage = 'message' in error ? error.message : null;
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
      return maybeMessage;
    }
  }

  return 'Unknown error';
}

function isJsonRecord(value: Json | null | undefined): value is Record<string, Json | undefined> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, Json | undefined> | null | undefined, key: string): string | null {
  if (!record) return null;
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function readNumber(record: Record<string, Json | undefined> | null | undefined, key: string): number | null {
  if (!record) return null;
  return parseFiniteNumber(record[key]);
}

function readJobBlockSnapshot(job: Job): Record<string, Json | undefined> | null {
  if (!isJsonRecord(job.snapshot)) {
    return null;
  }

  const block = job.snapshot.work_block;
  return isJsonRecord(block) ? block : null;
}

function readJobSiteSnapshot(job: Job): Record<string, Json | undefined> | null {
  if (!isJsonRecord(job.snapshot)) {
    return null;
  }

  const site = job.snapshot.site;
  return isJsonRecord(site) ? site : null;
}

function buildJobRow(
  job: Job,
  siteById: Map<string, Site>,
  blocksForJob: readonly JobBlock[],
): JobListRow {
  const liveSite = siteById.get(job.site_id);
  const snapshotSite = readJobSiteSnapshot(job);
  const legacyJob = job as Job & { generation_run_id?: string | null };
  const blocks = [...blocksForJob]
    .sort((a, b) => a.sort_order - b.sort_order || a.source_code.localeCompare(b.source_code))
    .map((block) => ({
      id: block.id,
      workBlockId: block.work_block_id,
      generationRunId: block.generation_run_id,
      sourceCode: block.source_code,
      area: block.area,
      label: block.label,
      jobKind: block.job_kind,
      status: block.status,
      points: block.points,
      requiredPhotos: block.required_photos,
      sortOrder: block.sort_order,
    }));

  const fallbackBlock = blocks.length === 0 ? readJobBlockSnapshot(job) : null;
  const fallbackBlocks = fallbackBlock
    ? [{
        id: readString(fallbackBlock, 'id') ?? `${job.id}:fallback`,
        workBlockId: readString(fallbackBlock, 'id'),
        generationRunId: legacyJob.generation_run_id ?? null,
        sourceCode: readString(fallbackBlock, 'source_code') ?? '—',
        area: readString(fallbackBlock, 'area') ?? '—',
        label: readString(fallbackBlock, 'label') ?? '—',
        jobKind: 'recurring' as const,
        status: (
          job.status === 'cancelled'
            ? 'cancelled'
            : job.status === 'completed' || job.status === 'approved'
              ? 'completed'
              : 'planned'
        ) as JobBlock['status'],
        points: readNumber(fallbackBlock, 'points') ?? job.total_points ?? 0,
        requiredPhotos: readNumber(fallbackBlock, 'required_photos') ?? 0,
        sortOrder: 0,
      }]
    : [];
  const resolvedBlocks = blocks.length > 0 ? blocks : fallbackBlocks;
  const generationRunIds = [...new Set([
    ...resolvedBlocks.map((block) => block.generationRunId).filter((id): id is string => Boolean(id)),
    ...(legacyJob.generation_run_id ? [legacyJob.generation_run_id] : []),
  ])];
  const totalPoints = resolvedBlocks.length > 0
    ? Math.round(resolvedBlocks.reduce((sum, block) => sum + block.points, 0) * 100) / 100
    : job.total_points ?? 0;

  return {
    id: job.id,
    siteId: job.site_id,
    siteName: liveSite?.name ?? readString(snapshotSite, 'name') ?? job.site_id,
    scheduledFor: job.scheduled_for,
    generationRunIds,
    status: job.status,
    assignedUserId: job.assigned_user_id,
    totalPoints,
    completedAt: job.completed_at,
    approvedAt: job.approved_at,
    approvedBy: job.approved_by,
    completionNotes: job.completion_notes,
    blocks: resolvedBlocks,
  };
}

function emptySiteDraft(): SiteGeneralDraft {
  return {
    id: createDraftId('site'),
    name: '',
    address: '',
    postal_code: '',
    service_start_date: '',
    status: 'active',
    active: true,
    customer_number: '',
    building_count: '',
    floor_count: '',
    apartment_count: '',
    key_info: '',
    key_count: '',
    cleaning_room_instructions: '',
    internal_notes: '',
    pin_code: '',
  };
}

function siteToDraft(site: Site): SiteGeneralDraft {
  return {
    id: site.id,
    name: site.name,
    address: site.address,
    postal_code: site.postal_code ?? '',
    service_start_date: site.service_start_date,
    status: site.status,
    active: site.active,
    customer_number: site.customer_number ?? '',
    building_count: site.building_count == null ? '' : String(site.building_count),
    floor_count: site.floor_count == null ? '' : String(site.floor_count),
    apartment_count: site.apartment_count == null ? '' : String(site.apartment_count),
    key_info: site.key_info ?? '',
    key_count: site.key_count == null ? '' : String(site.key_count),
    cleaning_room_instructions: site.cleaning_room_instructions ?? '',
    internal_notes: site.internal_notes ?? '',
    pin_code: site.pin_code ?? '',
  };
}

function normalizeSiteDraft(draft: SiteGeneralDraft): SiteGeneralDraft {
  return {
    ...draft,
    name: draft.name.trim(),
    address: draft.address.trim(),
    postal_code: draft.postal_code.trim(),
    service_start_date: draft.service_start_date.trim(),
    customer_number: draft.customer_number.trim(),
    building_count: draft.building_count.trim(),
    floor_count: draft.floor_count.trim(),
    apartment_count: draft.apartment_count.trim(),
    key_info: draft.key_info.trim(),
    key_count: draft.key_count.trim(),
    cleaning_room_instructions: draft.cleaning_room_instructions.trim(),
    internal_notes: draft.internal_notes.trim(),
    pin_code: draft.pin_code.trim(),
  };
}

function parseOptionalInteger(value: string): number | null {
  if (!value.trim()) {
    return null;
  }

  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function draftToSitePayload(draft: SiteGeneralDraft) {
  return {
    id: draft.id,
    name: draft.name,
    address: draft.address,
    postal_code: draft.postal_code || null,
    service_start_date: draft.service_start_date,
    status: draft.status,
    active: draft.active,
    customer_number: draft.customer_number || null,
    building_count: parseOptionalInteger(draft.building_count),
    floor_count: parseOptionalInteger(draft.floor_count),
    apartment_count: parseOptionalInteger(draft.apartment_count),
    key_info: draft.key_info || null,
    key_count: parseOptionalInteger(draft.key_count),
    cleaning_room_instructions: draft.cleaning_room_instructions || null,
    internal_notes: draft.internal_notes || null,
    pin_code: draft.pin_code || null,
  };
}

function validateSiteDraft(draft: SiteGeneralDraft) {
  if (!draft.name) return 'BRF name is required.';
  if (!draft.address) return 'Address is required.';
  if (!draft.service_start_date) return 'Service start date is required.';
  return null;
}

function emptyBlockDraft(siteId: string): OpsRuleDraft {
  return {
    id: createDraftId('block'),
    site_id: siteId,
    source_code: '',
    model_type: 'RECURRING_BLOCK',
    area: '',
    label: '',
    structured_tasks: [],
    cadence: 'WEEKLY',
    active_months: [...allMonthValues],
    weekdays: [2],
    parity: null,
    week_in_month: null,
    exact_months: [],
    first_visit_of_month: false,
    required_photos: 0,
    default_user_id: null,
    one_off_date: null,
    sort_order: 0,
    active: true,
    points: 0,
  };
}

function emptyExtraBlockDraft(siteId: string): OpsRuleDraft {
  return {
    id: createDraftId('block'),
    site_id: siteId,
    source_code: '',
    model_type: 'EXTRA_CLEANING_TEMPLATE',
    area: '',
    label: '',
    structured_tasks: [],
    cadence: 'EXACT_MONTHS',
    active_months: [],
    weekdays: [2],
    parity: null,
    week_in_month: '1',
    exact_months: [],
    first_visit_of_month: false,
    required_photos: 0,
    default_user_id: null,
    one_off_date: null,
    sort_order: 0,
    active: true,
    points: 0,
  };
}

function emptyOneOffBlockDraft(siteId: string): OpsRuleDraft {
  return {
    id: createDraftId('block'),
    site_id: siteId,
    source_code: '',
    model_type: 'EXTRA_CLEANING_TEMPLATE',
    area: '',
    label: '',
    structured_tasks: [],
    cadence: 'ONE_OFF',
    active_months: [],
    weekdays: [],
    parity: null,
    week_in_month: null,
    exact_months: [],
    first_visit_of_month: false,
    required_photos: 0,
    default_user_id: null,
    one_off_date: null,
    sort_order: 0,
    active: true,
    points: 0,
  };
}

function blockToDraft(block: WorkBlock): OpsRuleDraft {
  return {
    id: block.id,
    site_id: block.site_id,
    source_code: block.source_code,
    model_type: block.model_type,
    area: block.area,
    label: block.label,
    cadence: block.cadence,
    active_months: [...block.active_months],
    weekdays: [...block.weekdays],
    parity: block.parity,
    week_in_month: block.week_in_month,
    exact_months: [...block.exact_months],
    first_visit_of_month: block.first_visit_of_month,
    required_photos: block.required_photos,
    default_user_id: block.default_user_id,
    one_off_date: block.one_off_date ?? null,
    sort_order: block.sort_order,
    active: block.active,
    points: block.points ?? 0,
    structured_tasks: block.structured_tasks?.map((t) => ({ id: t.id, label: t.label, points: t.points })) ?? [],
  };
}

function normalizeBlockDraft(draft: OpsRuleDraft): OpsRuleDraft {
  const structuredTasks = draft.structured_tasks
    .map((task, index) => ({
      id: task.id || `task-${index}`,
      label: task.label.trim(),
      points: Number.isFinite(task.points) ? task.points : 0,
    }))
    .filter((task) => task.label);

  return {
    ...draft,
    source_code: draft.source_code.trim(),
    area: draft.area.trim(),
    label: draft.label.trim(),
    structured_tasks: structuredTasks,
    points: Math.round(structuredTasks.reduce((sum, t) => sum + t.points, 0) * 100) / 100,
    active_months: [...new Set(draft.active_months)].sort((a, b) => a - b),
    weekdays: [...new Set(draft.weekdays)].sort((a, b) => a - b),
    exact_months: [...new Set(draft.exact_months)].sort((a, b) => a - b),
    required_photos: Number.isFinite(draft.required_photos) ? Math.max(0, Math.floor(draft.required_photos)) : 0,
    sort_order: Number.isFinite(draft.sort_order) ? Math.max(0, Math.floor(draft.sort_order)) : 0,
  };
}

function validateBlockDrafts(blocks: OpsRuleDraft[]) {
  const seen = new Set<string>();

  for (const block of blocks) {
    if (!block.source_code.trim()) {
      return 'Every block needs a source code.';
    }

    if (seen.has(block.source_code.trim())) {
      return `Duplicate block code: ${block.source_code.trim()}`;
    }

    seen.add(block.source_code.trim());
  }

  return null;
}

function normalizeWeekInMonthForGenerator(
  value: OpsRuleDraft['week_in_month'],
): GeneratedOpsWorkBlock['week_in_month'] {
  if (value == null) {
    return null;
  }

  if (value === 'LAST') {
    return value;
  }

  const numeric = Number(value);
  return numeric === 1 || numeric === 2 || numeric === 3 || numeric === 4 ? numeric : null;
}

function buildPreviewSummary(
  siteDraft: SiteGeneralDraft,
  blockDrafts: OpsRuleDraft[],
  year: number,
  month: number,
): PreviewSummary {
  const site: GeneratedOpsSite = {
    id: siteDraft.id ?? createDraftId('site'),
    name: siteDraft.name,
    contract_path: '',
    service_start_date: siteDraft.service_start_date,
    purpose: '',
    notes: [],
    active: siteDraft.active,
  };

  const blocks: GeneratedOpsWorkBlock[] = blockDrafts.map((block) => ({
    id: block.id ?? createDraftId('block'),
    site_id: site.id,
    source_code: block.source_code,
    model_type: block.model_type,
    area: block.area,
    label: block.label,
    cadence: block.cadence,
    active_months: block.active_months,
    weekdays: block.weekdays as GeneratedOpsWorkBlock['weekdays'],
    parity: block.parity,
    week_in_month: normalizeWeekInMonthForGenerator(block.week_in_month),
    exact_months: block.exact_months,
    first_visit_of_month: block.first_visit_of_month,
    required_photos: block.required_photos,
    default_user_id: block.default_user_id,
    sort_order: block.sort_order,
    active: block.active,
    points: block.points ?? 0,
    structured_tasks: block.structured_tasks,
  }));

  const jobs = generateMonthlyOpsJobs({
    year,
    month,
    sites: [site],
    blocks,
  });

  const recurring = jobs.filter((job) => job.job_kind === 'recurring').length;
  const extra = jobs.filter((job) => job.job_kind === 'extra').length;

  return {
    jobs,
    recurring,
    extra,
    total: jobs.length,
  };
}

function roundPoints(value: number) {
  return Math.round(value * 100) / 100;
}

function normalizeNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => parseFiniteNumber(item))
    .filter((item): item is number => item != null);
}

function normalizeLiveSite(site: Site): Site {
  return {
    ...site,
    building_count: parseFiniteNumber(site.building_count),
    floor_count: parseFiniteNumber(site.floor_count),
    apartment_count: parseFiniteNumber(site.apartment_count),
    key_count: parseFiniteNumber(site.key_count),
  };
}

function normalizeLiveWorkBlock(block: WorkBlock): WorkBlock {
  const structuredTasks = normalizeStructuredTasks((block.structured_tasks ?? []) as GeneratedOpsWorkBlock['structured_tasks']);

  return {
    ...block,
    active_months: normalizeNumberArray(block.active_months),
    weekdays: normalizeNumberArray(block.weekdays),
    exact_months: normalizeNumberArray(block.exact_months),
    required_photos: coerceFiniteNumber(block.required_photos),
    sort_order: coerceFiniteNumber(block.sort_order),
    points: coerceFiniteNumber(block.points, computeBlockPoints(structuredTasks)),
    structured_tasks: structuredTasks as WorkBlock['structured_tasks'],
  };
}

function normalizeLiveGenerationRun(run: GenerationRun): GenerationRun {
  return {
    ...run,
    target_year: coerceFiniteNumber(run.target_year),
    target_month: coerceFiniteNumber(run.target_month),
  };
}

function normalizeLiveJob(job: Job): Job {
  return {
    ...job,
    total_points: coerceFiniteNumber(job.total_points),
  };
}

function normalizeLiveJobBlock(block: JobBlock): JobBlock {
  const structuredTasks = normalizeStructuredTasks((block.structured_tasks ?? []) as GeneratedOpsWorkBlock['structured_tasks']);

  return {
    ...block,
    points: coerceFiniteNumber(block.points, computeBlockPoints(structuredTasks)),
    sort_order: coerceFiniteNumber(block.sort_order),
    required_photos: coerceFiniteNumber(block.required_photos),
    structured_tasks: structuredTasks as JobBlock['structured_tasks'],
  };
}

function emptyTeamMemberPeriodStats(): TeamMemberPeriodStats {
  return {
    visits: 0,
    completedVisits: 0,
    approvedVisits: 0,
    pendingApprovalVisits: 0,
    cancelledVisits: 0,
    blocks: 0,
    scheduledPoints: 0,
    completedPoints: 0,
    approvedPoints: 0,
    pendingApprovalPoints: 0,
    cancelledPoints: 0,
  };
}

function buildTeamMemberPeriodStats(rows: readonly JobListRow[]): TeamMemberPeriodStats {
  const stats = emptyTeamMemberPeriodStats();

  for (const row of rows) {
    stats.visits += 1;
    stats.blocks += row.blocks.length;

    if (row.status === 'cancelled') {
      stats.cancelledVisits += 1;
      stats.cancelledPoints += row.totalPoints;
      continue;
    }

    stats.scheduledPoints += row.totalPoints;

    if (row.status === 'completed' || row.status === 'approved') {
      stats.completedVisits += 1;
      stats.completedPoints += row.totalPoints;
    }

    if (row.status === 'approved') {
      stats.approvedVisits += 1;
      stats.approvedPoints += row.totalPoints;
    }

    if (row.status === 'completed') {
      stats.pendingApprovalVisits += 1;
      stats.pendingApprovalPoints += row.totalPoints;
    }
  }

  return {
    ...stats,
    scheduledPoints: roundPoints(stats.scheduledPoints),
    completedPoints: roundPoints(stats.completedPoints),
    approvedPoints: roundPoints(stats.approvedPoints),
    pendingApprovalPoints: roundPoints(stats.pendingApprovalPoints),
    cancelledPoints: roundPoints(stats.cancelledPoints),
  };
}

function formatRoleLabel(role: PlanningUserRecord['role']) {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function getUserDisplayName(user: PlanningUserRecord | null | undefined) {
  if (!user) {
    return 'Unknown user';
  }

  return user.full_name?.trim() || user.email;
}

function formatMonthKey(year: number, month: number) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function matchesMonth(value: string, year: number, month: number) {
  return value.startsWith(formatMonthKey(year, month));
}

function formatSchedule(block: OpsRuleDraft | WorkBlock, language: Language) {
  const formatMonths = (months: readonly number[]) =>
    months.length === 0
      ? '—'
      : months.map((month) => formatMonthLabel(month, language).slice(0, 3)).join(', ');

  const weekdays = block.weekdays.length === 0
    ? '—'
    : block.weekdays.map((weekday) => formatWeekdayShortLabel(weekday)).join(', ');

  if (block.cadence === 'WEEKLY') {
    return `${weekdays} • ${formatMonths(block.active_months)}`;
  }

  if (block.cadence === 'BIWEEKLY') {
    return `${weekdays} • ${block.parity ?? '—'} • ${formatMonths(block.active_months)}`;
  }

  if (block.cadence === 'MONTHLY') {
    return `${weekdays} • week ${block.week_in_month ?? '—'} • ${formatMonths(block.active_months)}`;
  }

  if (block.cadence === 'ONE_OFF') {
    return `One-off: ${block.one_off_date ?? '—'}`;
  }

  return `${weekdays} • week ${block.week_in_month ?? '—'} • ${formatMonths(block.exact_months)}`;
}

function groupCalendarItems(rows: readonly JobListRow[]): Map<string, CalendarItem[]> {
  const groupedItems = new Map<string, CalendarItem[]>();

  for (const row of rows) {
    const current = groupedItems.get(row.scheduledFor) ?? [];
    current.push({
      id: row.id,
      siteId: row.siteId,
      siteName: row.siteName,
      status: row.status,
      totalPoints: row.totalPoints,
      blockCount: row.blocks.length,
      blocks: row.blocks.map((block) => ({
        id: block.id,
        code: block.sourceCode,
        area: block.area,
        label: block.label,
        points: block.points,
      })),
    });
    groupedItems.set(row.scheduledFor, current);
  }

  for (const [key, items] of groupedItems.entries()) {
    groupedItems.set(
      key,
      [...items].sort(
        (a, b) =>
          a.siteName.localeCompare(b.siteName) ||
          a.status.localeCompare(b.status),
      ),
    );
  }

  return groupedItems;
}

function statusBadgeVariant(status: Job['status'] | JobBlock['status']): 'default' | 'secondary' | 'warning' | 'success' | 'destructive' | 'outline' {
  if (status === 'completed' || status === 'approved') {
    return 'success';
  }

  if (status === 'cancelled') {
    return 'destructive';
  }

  if (status === 'skipped') {
    return 'warning';
  }

  if (status === 'assigned') {
    return 'secondary';
  }

  return 'warning';
}

function statusInk(status: Job['status'] | JobBlock['status']): string {
  if (status === 'completed' || status === 'approved') {
    return '#1B7F4D';
  }
  if (status === 'cancelled') {
    return '#C24138';
  }
  if (status === 'skipped') {
    return '#B97E1F';
  }
  if (status === 'assigned') {
    return '#073331';
  }
  return '#B97E1F';
}

function siteStatusBadgeVariant(status: Site['status']): 'warning' | 'success' | 'destructive' | 'outline' {
  if (status === 'active') {
    return 'success';
  }

  if (status === 'paused') {
    return 'warning';
  }

  if (status === 'archived') {
    return 'outline';
  }

  return 'outline';
}

function canApproveVisit(row: JobListRow) {
  return row.status === 'completed'
    && row.blocks.length > 0
    && row.blocks.every((block) => block.status !== 'planned')
    && row.blocks.some((block) => block.status === 'completed');
}

function calendarItemClass(item: CalendarItem) {
  if (item.status === 'cancelled') {
    return 'border-l-destructive bg-destructive/5';
  }

  if (item.status === 'completed' || item.status === 'approved') {
    return 'border-l-chart-4 bg-chart-4/10';
  }

  return 'border-l-primary bg-primary/5';
}

async function fetchAllJobBlocks(): Promise<JobBlock[]> {
  const pageSize = 1000;
  const all: JobBlock[] = [];
  let from = 0;
  // Loop until response shorter than pageSize.
  // PostgREST caps server response at 1000 rows; paginate manually.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const to = from + pageSize - 1;
    const { data, error } = await supabase
      .from('job_blocks')
      .select('*')
      .order('job_id')
      .order('sort_order')
      .range(from, to);
    if (error) throw error;
    const batch = data ?? [];
    all.push(...batch);
    if (batch.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

function loadPlanningData(): Promise<PlanningLiveData> {
  return Promise.all([
    supabase.from('sites').select('*').order('name'),
    supabase.from('work_blocks').select('*').order('site_id').order('sort_order'),
    supabase.from('generation_runs').select('*').order('target_year').order('target_month').order('created_at'),
    supabase.from('jobs').select('*').order('scheduled_for').order('site_id'),
    fetchAllJobBlocks(),
  ]).then(([sitesResponse, workBlocksResponse, generationRunsResponse, jobsResponse, jobBlocksAll]) => {
    if (sitesResponse.error) throw sitesResponse.error;
    if (workBlocksResponse.error) throw workBlocksResponse.error;
    if (generationRunsResponse.error) throw generationRunsResponse.error;
    if (jobsResponse.error) throw jobsResponse.error;

    return {
      sites: (sitesResponse.data ?? []).map(normalizeLiveSite),
      workBlocks: (workBlocksResponse.data ?? []).map(normalizeLiveWorkBlock),
      generationRuns: (generationRunsResponse.data ?? []).map(normalizeLiveGenerationRun),
      jobs: (jobsResponse.data ?? []).map(normalizeLiveJob),
      jobBlocks: jobBlocksAll.map(normalizeLiveJobBlock),
      syncedAt: new Date().toISOString(),
    };
  });
}

function InlineNotice({ notice }: { notice: NoticeState | null }) {
  if (!notice) return null;

  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        notice.tone === 'success' && 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700',
        notice.tone === 'error' && 'border-destructive/30 bg-destructive/5 text-destructive',
        notice.tone === 'warning' && 'border-amber-500/30 bg-amber-500/5 text-amber-700',
      )}
    >
      {notice.text}
    </div>
  );
}

function MonthScopeControls({
  language,
  month,
  year,
  onMonthChange,
  onYearChange,
  onRefresh,
  isRefreshing,
  syncedAt,
  refreshLabel = 'Refresh',
  compact = false,
}: {
  language: Language;
  month: number;
  year: number;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  syncedAt?: string | null;
  refreshLabel?: string;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <>
        <Select value={String(month)} onValueChange={(value) => onMonthChange(Number(value))}>
          <SelectTrigger className="h-9 w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={String(option.value)}>
                {option.label[language]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          className="h-9 w-[88px]"
          type="number"
          value={year}
          onChange={(event) => onYearChange(Number(event.target.value) || year)}
        />
        {onRefresh ? (
          <Button type="button" size="sm" variant="outline" onClick={onRefresh} disabled={Boolean(isRefreshing)}>
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {refreshLabel}
          </Button>
        ) : null}
        {syncedAt ? (
          <span className="text-xs text-muted-foreground">
            {syncedAt.slice(0, 19).replace('T', ' ')}
          </span>
        ) : null}
      </>
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-2">
        <Label>Month</Label>
        <Select value={String(month)} onValueChange={(value) => onMonthChange(Number(value))}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={String(option.value)}>
                {option.label[language]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Year</Label>
        <Input
          className="w-[100px]"
          type="number"
          value={year}
          onChange={(event) => onYearChange(Number(event.target.value) || year)}
        />
      </div>
      {onRefresh ? (
        <Button type="button" variant="outline" onClick={onRefresh} disabled={Boolean(isRefreshing)}>
          {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {refreshLabel}
        </Button>
      ) : null}
      {syncedAt ? (
        <span className="pb-2 text-xs text-muted-foreground">
          Synced {syncedAt.slice(0, 19).replace('T', ' ')}
        </span>
      ) : null}
    </div>
  );
}

function CollectiveDayPopover({
  iso,
  items,
}: {
  iso: string;
  items: CalendarItem[];
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => cancelClose(), []);

  const totalPts = items.reduce((sum, i) => sum + i.totalPoints, 0);
  const totalBlocks = items.reduce((sum, i) => sum + i.blockCount, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          onMouseEnter={() => {
            cancelClose();
            setOpen(true);
          }}
          onMouseLeave={scheduleClose}
          className="cursor-default rounded-sm border border-border/60 bg-muted/40 px-2 py-1.5 text-[11px] hover:bg-muted/70"
        >
          <div className="font-medium">{items.length} BRF</div>
          <div className="text-muted-foreground">
            {totalPts.toFixed(2)} pts · {totalBlocks} block{totalBlocks === 1 ? '' : 's'}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-72 p-2"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-2 px-1 text-xs font-medium text-muted-foreground">
          {iso} · {items.length} BRF
        </div>
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {items.map((item) => (
            <Tooltip key={item.id} delayDuration={120}>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'rounded-sm border-l-2 px-2 py-1 text-[11px]',
                    calendarItemClass(item),
                  )}
                >
                  <div className="truncate font-medium">{item.siteName}</div>
                  <div className="truncate text-muted-foreground">
                    {item.blockCount > 0
                      ? `${item.blockCount} block${item.blockCount === 1 ? '' : 's'} • ${item.totalPoints.toFixed(2)} pts`
                      : `${item.totalPoints.toFixed(2)} pts`}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" align="start" className="max-w-[320px]">
                <div className="space-y-2">
                  <div className="font-medium">{item.siteName}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.blockCount > 0
                      ? `${item.blockCount} block${item.blockCount === 1 ? '' : 's'} • ${item.totalPoints.toFixed(2)} pts`
                      : `Visit • ${item.totalPoints.toFixed(2)} pts`}
                  </div>
                  {item.blocks.length > 0 ? (
                    <div className="space-y-1">
                      {item.blocks.map((block) => (
                        <div key={block.id} className="text-xs">
                          <div className="font-medium">{block.code}</div>
                          <div className="text-muted-foreground">
                            {block.area} • {block.label} • {block.points.toFixed(2)} pts
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">Block details unavailable.</div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MonthGridCalendar({
  language,
  year,
  month,
  itemsByDate,
  showSiteName,
}: {
  language: Language;
  year: number;
  month: number;
  itemsByDate: Map<string, CalendarItem[]>;
  showSiteName: boolean;
}) {
  const dates = getMonthDates(year, month);
  const leadingSlots = dates.length > 0 ? getIsoWeekday(dates[0]) - 1 : 0;
  const cells: Array<Date | null> = [
    ...Array.from({ length: leadingSlots }, () => null),
    ...dates,
  ];

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
      <div className="min-w-[820px]">
        <div className="grid grid-cols-7 border-x border-t border-border/70">
          {weekdayOptions.map((option) => (
            <div
              key={option.value}
              className="border-b border-r border-border/70 bg-muted/30 px-3 py-2 text-xs font-medium text-muted-foreground last:border-r-0"
            >
              {option.long[language]}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 border-x border-b border-border/70">
          {cells.map((date, index) => {
            if (!date) {
              return (
                <div
                  key={`empty-${index}`}
                  className="min-h-[150px] border-r border-t border-border/50 bg-muted/10 last:border-r-0"
                />
              );
            }

            const iso = date.toISOString().slice(0, 10);
            const items = itemsByDate.get(iso) ?? [];

            return (
              <div
                key={iso}
                className="min-h-[150px] border-r border-t border-border/50 p-2 last:border-r-0"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{iso.slice(-2)}</span>
                  {items.length > 0 ? (
                    <span className="text-[11px] text-muted-foreground">{items.length}</span>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  {items.length >= 4 ? (
                    <CollectiveDayPopover iso={iso} items={items} />
                  ) : (
                    items.map((item) => (
                      <Tooltip key={item.id}>
                        <TooltipTrigger asChild>
                          <div
                            className={cn('rounded-sm border-l-2 px-2 py-1 text-[11px]', calendarItemClass(item))}
                          >
                            <div className="truncate font-medium">{item.siteName}</div>
                            <div className="truncate text-muted-foreground">
                              {item.blockCount > 0
                                ? `${item.blockCount} block${item.blockCount === 1 ? '' : 's'} • ${item.totalPoints.toFixed(2)} pts`
                                : `${item.totalPoints.toFixed(2)} pts`}
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="start" className="max-w-[320px]">
                          <div className="space-y-2">
                            <div className="font-medium">{item.siteName}</div>
                            <div className="text-xs text-muted-foreground">
                              {item.blockCount > 0
                                ? `${item.blockCount} block${item.blockCount === 1 ? '' : 's'} • ${item.totalPoints.toFixed(2)} pts`
                                : `Visit • ${item.totalPoints.toFixed(2)} pts`}
                            </div>
                            {item.blocks.length > 0 ? (
                              <div className="space-y-1">
                                {item.blocks.map((block) => (
                                  <div key={block.id} className="text-xs">
                                    <div className="font-medium">{block.code}</div>
                                    <div className="text-muted-foreground">
                                      {block.area} • {block.label} • {block.points.toFixed(2)} pts
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-muted-foreground">Block details unavailable.</div>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </TooltipProvider>
  );
}

export function PlanningWorkspace({ language }: PlanningWorkspaceProps) {
  const { session, role } = useAuth();
  const sessionToken = session?.access_token ?? null;
  const now = new Date();
  const [route, setRoute] = useState<PlanningRoute>(parsePlanningRoute());
  const [year, setYear] = useState<number>(now.getFullYear());
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [data, setData] = useState<PlanningLiveData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [siteSearch, setSiteSearch] = useState('');
  const [siteStatusFilter, setSiteStatusFilter] = useState<'all' | Site['status']>('all');
  const initialRoute = parsePlanningRoute();
  const [calendarSiteFilter, setCalendarSiteFilter] = useState<string>(
    initialRoute.page === 'calendar' && initialRoute.calendarSite ? initialRoute.calendarSite : ALL_SITES,
  );
  const [calendarStatusFilter, setCalendarStatusFilter] = useState<string>(
    initialRoute.page === 'calendar' && initialRoute.calendarStatus ? initialRoute.calendarStatus : ALL_JOB_STATUSES,
  );
  const [calendarCleanerFilter, setCalendarCleanerFilter] = useState<string>(
    initialRoute.page === 'calendar' && initialRoute.calendarCleaner ? initialRoute.calendarCleaner : ALL_CLEANERS,
  );
  const [visitRowsSiteFilter, setVisitRowsSiteFilter] = useState<string>('');
  const [isVisitRowsSitePickerOpen, setIsVisitRowsSitePickerOpen] = useState(false);
  const [siteDraft, setSiteDraft] = useState<SiteGeneralDraft>(emptySiteDraft());
  const [siteBaseline, setSiteBaseline] = useState<SiteGeneralDraft>(emptySiteDraft());
  const [blockDrafts, setBlockDrafts] = useState<OpsRuleDraft[]>([]);
  const [blockBaseline, setBlockBaseline] = useState<OpsRuleDraft[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [isSavingSite, setIsSavingSite] = useState(false);
  const [isSavingBlocks, setIsSavingBlocks] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [users, setUsers] = useState<PlanningUserRecord[]>([]);
  const [userDrafts, setUserDrafts] = useState<Record<string, UserUpdateDraft>>({});
  const [createUserDraft, setCreateUserDraft] = useState<UserCreateDraft>({
    email: '',
    password: '',
    full_name: '',
    role: 'cleaner',
    active: true,
  });
  const [teamSearch, setTeamSearch] = useState('');
  const [teamRoleFilter, setTeamRoleFilter] = useState<'all' | PlanningUserRecord['role']>('all');
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [approvingJobId, setApprovingJobId] = useState<string | null>(null);
  const [reassigningJobId, setReassigningJobId] = useState<string | null>(null);
  const [absences, setAbsences] = useState<CleanerAbsenceRecord[]>([]);
  const [isAbsencesLoading, setIsAbsencesLoading] = useState(false);
  const [absenceDraft, setAbsenceDraft] = useState<{
    id: string | null;
    startsOn: string;
    endsOn: string;
    substituteUserId: string | null;
    reason: string;
  } | null>(null);
  const [isSavingAbsence, setIsSavingAbsence] = useState(false);
  const [deletingAbsenceId, setDeletingAbsenceId] = useState<string | null>(null);
  // Per-site avvikelser, fetched once; keyed by site_id. Degrades to empty on failure.
  const [avvikelserMap, setAvvikelserMap] = useState<Map<string, PortfolioFlag[]>>(new Map());

  const canManageUsers = role === 'admin';
  const canApproveJobs = role === 'admin' || role === 'supervisor';
  const canAssignCleaners = role === 'admin' || role === 'supervisor';

  useEffect(() => {
    const handleHashChange = () => {
      const next = parsePlanningRoute();
      setRoute(next);
      // Restore filters when navigating back to calendar via browser back/forward
      if (next.page === 'calendar') {
        setCalendarSiteFilter(next.calendarSite ?? ALL_SITES);
        setCalendarStatusFilter(next.calendarStatus ?? ALL_JOB_STATUSES);
        setCalendarCleanerFilter(next.calendarCleaner ?? ALL_CLEANERS);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Sync calendar filters → URL hash
  useEffect(() => {
    if (route.page !== 'calendar') return;
    const params = new URLSearchParams();
    if (calendarSiteFilter !== ALL_SITES) params.set('site', calendarSiteFilter);
    if (calendarCleanerFilter !== ALL_CLEANERS) params.set('cleaner', calendarCleanerFilter);
    if (calendarStatusFilter !== ALL_JOB_STATUSES) params.set('status', calendarStatusFilter);
    const qs = params.toString();
    const newHash = qs ? `#/calendar?${qs}` : '#/calendar';
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [route.page, calendarSiteFilter, calendarStatusFilter, calendarCleanerFilter]);

  useEffect(() => {
    if ((route.page === 'team' || route.page === 'team-member') && !canManageUsers) {
      navigateToRoute({ page: 'brfs' });
    }
  }, [route.page, canManageUsers]);

  const refreshLiveData = async () => {
    setIsRefreshing(true);
    try {
      const nextData = await loadPlanningData();
      setData(nextData);
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const nextData = await loadPlanningData();
        if (!cancelled) {
          setData(nextData);
        }
      } catch (error) {
        if (!cancelled) {
          setNotice({ tone: 'error', text: getErrorMessage(error) });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const refreshUsers = async () => {
    if (!sessionToken || (role !== 'admin' && role !== 'supervisor')) {
      setUsers([]);
      setUserDrafts({});
      return;
    }

    setIsUsersLoading(true);
    try {
      const nextUsers = await listOpsUsers(sessionToken);
      setUsers(nextUsers);
      setUserDrafts(
        Object.fromEntries(
          nextUsers.map((user) => [
            user.id,
            {
              full_name: user.full_name ?? '',
              role: user.role,
              active: user.active,
            },
          ]),
        ),
      );
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setIsUsersLoading(false);
    }
  };

  useEffect(() => {
    void refreshUsers();
  }, [sessionToken, role]);

  useEffect(() => {
    if (route.page === 'team-member' && route.tab === 'vacations' && canAssignCleaners && sessionToken) {
      void refreshAbsences(route.userId);
      setAbsenceDraft(null);
    } else {
      setAbsences([]);
      setAbsenceDraft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, sessionToken, canAssignCleaners]);

  // Fetch per-site avvikelser once per session token. Silent on failure.
  useEffect(() => {
    if (!sessionToken) {
      setAvvikelserMap(new Map());
      return;
    }
    let cancelled = false;
    void fetchSiteAvvikelser(sessionToken)
      .then((rows) => {
        if (cancelled) return;
        setAvvikelserMap(new Map(rows.map((r) => [r.site_id, r.flags])));
      })
      .catch(() => {
        if (!cancelled) setAvvikelserMap(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [sessionToken]);

  const sites = data?.sites ?? [];
  const workBlocks = data?.workBlocks ?? [];
  const generationRuns = data?.generationRuns ?? [];
  const jobs = data?.jobs ?? [];
  const jobBlocks = data?.jobBlocks ?? [];

  const siteById = useMemo(() => new Map(sites.map((site) => [site.id, site])), [sites]);
  const workBlocksBySite = useMemo(() => {
    const map = new Map<string, WorkBlock[]>();
    for (const block of workBlocks) {
      const current = map.get(block.site_id) ?? [];
      current.push(block);
      map.set(block.site_id, current);
    }

    for (const blocks of map.values()) {
      blocks.sort((a, b) => a.sort_order - b.sort_order || a.source_code.localeCompare(b.source_code));
    }

    return map;
  }, [workBlocks]);
  const generationRunById = useMemo(
    () => new Map(generationRuns.map((run) => [run.id, run])),
    [generationRuns],
  );
  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );
  const cleanerUsers = useMemo(
    () => users.filter((user) => user.role === 'cleaner' && user.active),
    [users],
  );
  const jobBlocksByJobId = useMemo(() => {
    const map = new Map<string, JobBlock[]>();
    for (const block of jobBlocks) {
      const current = map.get(block.job_id) ?? [];
      current.push(block);
      map.set(block.job_id, current);
    }
    return map;
  }, [jobBlocks]);

  const jobRows = useMemo(
    () =>
      jobs
        .map((job) => buildJobRow(job, siteById, jobBlocksByJobId.get(job.id) ?? []))
        .sort(
          (a, b) =>
            a.scheduledFor.localeCompare(b.scheduledFor) ||
            a.siteName.localeCompare(b.siteName) ||
            a.id.localeCompare(b.id),
        ),
    [jobs, jobBlocksByJobId, siteById],
  );

  const monthJobRows = useMemo(
    () => jobRows.filter((row) => matchesMonth(row.scheduledFor, year, month)),
    [jobRows, year, month],
  );

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const teamJobRowsByUserId = useMemo(() => {
    const map = new Map<string, JobListRow[]>();

    for (const row of jobRows) {
      if (!row.assignedUserId) {
        continue;
      }

      const current = map.get(row.assignedUserId) ?? [];
      current.push(row);
      map.set(row.assignedUserId, current);
    }

    return map;
  }, [jobRows]);
  const monthTeamJobRowsByUserId = useMemo(() => {
    const map = new Map<string, JobListRow[]>();

    for (const row of monthJobRows) {
      if (!row.assignedUserId) {
        continue;
      }

      const current = map.get(row.assignedUserId) ?? [];
      current.push(row);
      map.set(row.assignedUserId, current);
    }

    return map;
  }, [monthJobRows]);
  const teamMemberSummaries = useMemo(() => {
    const roleOrder: Record<PlanningUserRecord['role'], number> = {
      cleaner: 0,
      supervisor: 1,
      admin: 2,
    };

    return users
      .map((user) => {
        const rows = teamJobRowsByUserId.get(user.id) ?? [];
        const monthRows = monthTeamJobRowsByUserId.get(user.id) ?? [];
        const scheduledRows = [...rows].sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
        const siteCount = new Set(rows.map((row) => row.siteId)).size;
        const nextVisit = scheduledRows.find((row) => row.scheduledFor >= todayIso)?.scheduledFor ?? null;
        const lastVisit = [...scheduledRows].reverse().find((row) => row.scheduledFor <= todayIso)?.scheduledFor
          ?? scheduledRows.at(-1)?.scheduledFor
          ?? null;

        return {
          user,
          month: buildTeamMemberPeriodStats(monthRows),
          lifetime: buildTeamMemberPeriodStats(rows),
          siteCount,
          nextVisit,
          lastVisit,
        } satisfies TeamMemberSummary;
      })
      .sort((left, right) =>
        roleOrder[left.user.role] - roleOrder[right.user.role]
        || getUserDisplayName(left.user).localeCompare(getUserDisplayName(right.user))
        || left.user.email.localeCompare(right.user.email));
  }, [monthTeamJobRowsByUserId, teamJobRowsByUserId, todayIso, users]);
  const teamSummaryByUserId = useMemo(
    () => new Map(teamMemberSummaries.map((summary) => [summary.user.id, summary])),
    [teamMemberSummaries],
  );
  const filteredTeamMemberSummaries = useMemo(() => {
    const search = teamSearch.trim().toLowerCase();

    return teamMemberSummaries.filter((summary) => {
      const matchesRole = teamRoleFilter === 'all' || summary.user.role === teamRoleFilter;
      const haystack = `${getUserDisplayName(summary.user)} ${summary.user.email} ${summary.user.role}`.toLowerCase();
      const matchesSearch = search.length === 0 || haystack.includes(search);
      return matchesRole && matchesSearch;
    });
  }, [teamMemberSummaries, teamRoleFilter, teamSearch]);
  const selectedTeamMember = useMemo(
    () => route.page === 'team-member' ? (usersById.get(route.userId) ?? null) : null,
    [route, usersById],
  );
  const selectedTeamSummary = useMemo(
    () => (selectedTeamMember ? (teamSummaryByUserId.get(selectedTeamMember.id) ?? null) : null),
    [selectedTeamMember, teamSummaryByUserId],
  );
  const selectedTeamMemberRows = useMemo(
    () => (selectedTeamMember ? (teamJobRowsByUserId.get(selectedTeamMember.id) ?? []) : []),
    [selectedTeamMember, teamJobRowsByUserId],
  );
  const selectedTeamMemberMonthRows = useMemo(
    () => (selectedTeamMember ? (monthTeamJobRowsByUserId.get(selectedTeamMember.id) ?? []) : []),
    [selectedTeamMember, monthTeamJobRowsByUserId],
  );
  const selectedApprovals = useMemo(
    () => (selectedTeamMember ? jobRows.filter((row) => row.approvedBy === selectedTeamMember.id) : []),
    [jobRows, selectedTeamMember],
  );
  const selectedMonthApprovals = useMemo(
    () => (selectedTeamMember ? monthJobRows.filter((row) => row.approvedBy === selectedTeamMember.id) : []),
    [monthJobRows, selectedTeamMember],
  );
  const unassignedMonthRows = useMemo(
    () => monthJobRows.filter((row) => !row.assignedUserId),
    [monthJobRows],
  );

  useEffect(() => {
    if (route.page !== 'team-member' || isUsersLoading) {
      return;
    }

    if (!selectedTeamMember) {
      setNotice({ tone: 'warning', text: 'Team member not found.' });
      navigateToRoute({ page: 'team' });
    }
  }, [route, isUsersLoading, selectedTeamMember]);

  const monthJobStatsBySite = useMemo(() => {
    const map = new Map<string, { total: number; points: number; blocks: number; runIds: Set<string> }>();

    for (const row of monthJobRows) {
      const current = map.get(row.siteId) ?? {
        total: 0,
        points: 0,
        blocks: 0,
        runIds: new Set<string>(),
      };

      current.total += 1;
      current.points += row.totalPoints;
      current.blocks += row.blocks.length;
      for (const runId of row.generationRunIds) {
        current.runIds.add(runId);
      }
      map.set(row.siteId, current);
    }

    return map;
  }, [monthJobRows]);

  const routeSiteId = route.page === 'site' ? route.siteId : null;
  const isNewSite = route.page === 'site' && route.siteId === NEW_SITE_ID;
  const selectedSite = route.page === 'site' && !isNewSite
    ? siteById.get(route.siteId) ?? null
    : null;
  const selectedLiveBlocks = selectedSite
    ? workBlocksBySite.get(selectedSite.id) ?? EMPTY_WORK_BLOCKS
    : EMPTY_WORK_BLOCKS;

  useEffect(() => {
    if (route.page !== 'site') {
      return;
    }

    setNotice(null);

    if (isNewSite) {
      const blankSite = emptySiteDraft();
      setSiteDraft(blankSite);
      setSiteBaseline(blankSite);
      setBlockDrafts([]);
      setBlockBaseline([]);
      setSelectedBlockId(null);
      return;
    }

    if (selectedSite) {
      const nextSite = siteToDraft(selectedSite);
      const nextBlocks = selectedLiveBlocks.map(blockToDraft);
      setSiteDraft(nextSite);
      setSiteBaseline(nextSite);
      setBlockDrafts(nextBlocks);
      setBlockBaseline(nextBlocks);
      setSelectedBlockId((current) =>
        nextBlocks.some((block) => block.id === current) ? current : (nextBlocks[0]?.id ?? null),
      );
      return;
    }

    if (!isLoading) {
      navigateToRoute({ page: 'brfs' });
    }
  }, [route.page, routeSiteId, isNewSite, selectedSite, selectedLiveBlocks, isLoading]);

  const normalizedSiteDraft = useMemo(() => normalizeSiteDraft(siteDraft), [siteDraft]);
  const normalizedBlockDrafts = useMemo(
    () => blockDrafts.map((block) => normalizeBlockDraft(block)),
    [blockDrafts],
  );

  const siteDirty = JSON.stringify(normalizedSiteDraft) !== JSON.stringify(normalizeSiteDraft(siteBaseline));
  const blocksDirty = JSON.stringify(normalizedBlockDrafts) !== JSON.stringify(blockBaseline.map((block) => normalizeBlockDraft(block)));
  const selectedBlock = blockDrafts.find((block) => block.id === selectedBlockId) ?? null;

  // Auto-select the first matching block when switching between Blocks/Extras tabs
  useEffect(() => {
    if (route.page !== 'site' || (route.tab !== 'blocks' && route.tab !== 'extras')) return;
    if (!selectedBlock) return;
    const isRecurring = selectedBlock.model_type === 'RECURRING_BLOCK';
    const isExtra = selectedBlock.model_type === 'EXTRA_CLEANING_TEMPLATE';
    if ((route.tab === 'blocks' && isRecurring) || (route.tab === 'extras' && isExtra)) return;
    // Current selection doesn't match this tab — auto-select the first matching block
    const matching = route.tab === 'blocks'
      ? blockDrafts.find((b) => b.model_type === 'RECURRING_BLOCK')
      : blockDrafts.find((b) => b.model_type === 'EXTRA_CLEANING_TEMPLATE');
    setSelectedBlockId(matching?.id ?? null);
  }, [route.tab, route.page]);
  const siteValidationError = validateSiteDraft(normalizedSiteDraft);
  const blockValidationError = validateBlockDrafts(normalizedBlockDrafts);
  const canSaveGeneral = Boolean(sessionToken && siteDirty && !siteValidationError && !isSavingSite);
  const canSaveBlocks = Boolean(
    sessionToken
      && (siteDirty || blocksDirty)
      && !siteValidationError
      && !blockValidationError
      && !isSavingBlocks,
  );
  const activeSiteIds = useMemo(
    () => sites.filter((site) => site.active).map((site) => site.id),
    [sites],
  );

  const previewState = useMemo(() => {
    if (siteValidationError) {
      return { error: siteValidationError, summary: null as PreviewSummary | null };
    }

    if (blockValidationError) {
      return { error: blockValidationError, summary: null as PreviewSummary | null };
    }

    try {
      return {
        error: null,
        summary: buildPreviewSummary(normalizedSiteDraft, normalizedBlockDrafts, year, month),
      };
    } catch (error) {
      return {
        error: getErrorMessage(error),
        summary: null as PreviewSummary | null,
      };
    }
  }, [siteValidationError, blockValidationError, normalizedSiteDraft, normalizedBlockDrafts, year, month]);

  const selectedSiteMonthJobs = useMemo(
    () => (selectedSite ? monthJobRows.filter((row) => row.siteId === selectedSite.id) : []),
    [monthJobRows, selectedSite],
  );
  const selectedSiteCalendarItems = useMemo(
    () => groupCalendarItems(selectedSiteMonthJobs),
    [selectedSiteMonthJobs],
  );
  const selectedSiteRunIds = useMemo(
    () => [...new Set(selectedSiteMonthJobs.flatMap((row) => row.generationRunIds))],
    [selectedSiteMonthJobs],
  );
  const selectedSiteRuns = useMemo(
    () =>
      selectedSiteRunIds
        .map((runId) => generationRunById.get(runId))
        .filter((run): run is GenerationRun => Boolean(run))
        .sort(
          (a, b) =>
            a.target_year - b.target_year ||
            a.target_month - b.target_month ||
            a.started_at.localeCompare(b.started_at),
        ),
    [selectedSiteRunIds, generationRunById],
  );

  const filteredSites = useMemo(() => {
    const query = siteSearch.trim().toLowerCase();
    return sites.filter((site) => {
      const matchesQuery = !query
        || `${site.name} ${site.address} ${site.customer_number ?? ''}`.toLowerCase().includes(query);
      const matchesStatus = siteStatusFilter === 'all' || site.status === siteStatusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [sites, siteSearch, siteStatusFilter]);

  const filteredGeneralCalendarJobs = useMemo(
    () =>
      monthJobRows.filter((row) => {
        const matchesSite = calendarSiteFilter === ALL_SITES || row.siteId === calendarSiteFilter;
        const matchesStatus = calendarStatusFilter === ALL_JOB_STATUSES || row.status === calendarStatusFilter;
        const matchesCleaner =
          calendarCleanerFilter === ALL_CLEANERS
          || (calendarCleanerFilter === UNASSIGNED_CLEANER ? !row.assignedUserId : row.assignedUserId === calendarCleanerFilter);
        return matchesSite && matchesStatus && matchesCleaner;
      }),
    [monthJobRows, calendarSiteFilter, calendarStatusFilter, calendarCleanerFilter],
  );
  const generalCalendarItems = useMemo(
    () => groupCalendarItems(filteredGeneralCalendarJobs),
    [filteredGeneralCalendarJobs],
  );
  const visitRowSiteOptions = useMemo(() => {
    const siteIds = new Set(
      monthJobRows
        .filter((row) => calendarStatusFilter === ALL_JOB_STATUSES || row.status === calendarStatusFilter)
        .map((row) => row.siteId),
    );

    return sites
      .filter((site) => siteIds.has(site.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [monthJobRows, calendarStatusFilter, sites]);
  const selectedVisitRowsSite = useMemo(
    () => visitRowSiteOptions.find((site) => site.id === visitRowsSiteFilter) ?? null,
    [visitRowSiteOptions, visitRowsSiteFilter],
  );
  const filteredVisitRows = useMemo(
    () =>
      monthJobRows.filter((row) => {
        const matchesStatus = calendarStatusFilter === ALL_JOB_STATUSES || row.status === calendarStatusFilter;
        const matchesSite = row.siteId === visitRowsSiteFilter;
        return matchesStatus && matchesSite;
      }),
    [monthJobRows, calendarStatusFilter, visitRowsSiteFilter],
  );
  const selectedJob = useMemo(
    () => jobRows.find((row) => row.id === selectedJobId) ?? null,
    [jobRows, selectedJobId],
  );

  useEffect(() => {
    const availableSiteIds = new Set(visitRowSiteOptions.map((site) => site.id));

    if (
      calendarSiteFilter !== ALL_SITES
      && availableSiteIds.has(calendarSiteFilter)
      && visitRowsSiteFilter !== calendarSiteFilter
    ) {
      setVisitRowsSiteFilter(calendarSiteFilter);
      return;
    }

    if (!visitRowsSiteFilter || !availableSiteIds.has(visitRowsSiteFilter)) {
      setVisitRowsSiteFilter(visitRowSiteOptions[0]?.id ?? '');
    }
  }, [calendarSiteFilter, visitRowSiteOptions, visitRowsSiteFilter]);

  const updateSiteDraft = (patch: Partial<SiteGeneralDraft>) => {
    setSiteDraft((current) => ({ ...current, ...patch }));
  };

  const updateUserDraft = (userId: string, patch: Partial<UserUpdateDraft>) => {
    setUserDrafts((current) => ({
      ...current,
      [userId]: {
        full_name: current[userId]?.full_name ?? '',
        role: current[userId]?.role ?? 'cleaner',
        active: current[userId]?.active ?? true,
        ...patch,
      },
    }));
  };

  const updateBlockDraft = (blockId: string, patch: Partial<OpsRuleDraft>) => {
    setBlockDrafts((current) =>
      current.map((block) => (block.id === blockId ? { ...block, ...patch } : block)),
    );
  };

  const submitCreateUser = async () => {
    if (!sessionToken || !canManageUsers) {
      setNotice({ tone: 'error', text: 'Only admins can create users.' });
      return;
    }

    if (!createUserDraft.email.trim() || !createUserDraft.password || !createUserDraft.full_name.trim()) {
      setNotice({ tone: 'error', text: 'Email, password, and full name are required.' });
      return;
    }

    setIsCreatingUser(true);
    setNotice(null);

    try {
      const user = await createOpsUser(
        {
          email: createUserDraft.email.trim(),
          password: createUserDraft.password,
          full_name: createUserDraft.full_name.trim(),
          role: createUserDraft.role,
          active: createUserDraft.active,
        },
        sessionToken,
      );
      await refreshUsers();
      setCreateUserDraft({
        email: '',
        password: '',
        full_name: '',
        role: 'cleaner',
        active: true,
      });
      setIsCreateUserDialogOpen(false);
      navigateToRoute({
        page: 'team-member',
        userId: user.id,
        tab: user.role === 'cleaner' ? 'points' : 'profile',
      });
      setNotice({ tone: 'success', text: `Created ${user.email}.` });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setIsCreatingUser(false);
    }
  };

  const submitUserUpdate = async (userId: string) => {
    if (!sessionToken || !canManageUsers) {
      setNotice({ tone: 'error', text: 'Only admins can update users.' });
      return;
    }

    const draft = userDrafts[userId];
    if (!draft) {
      return;
    }

    setSavingUserId(userId);
    setNotice(null);

    try {
      const user = await updateOpsUser(
        {
          id: userId,
          full_name: draft.full_name.trim(),
          role: draft.role,
          active: draft.active,
        },
        sessionToken,
      );
      await refreshUsers();
      setNotice({ tone: 'success', text: `Updated ${user.email}.` });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setSavingUserId(null);
    }
  };

  const submitApproveJob = async (jobId: string) => {
    if (!sessionToken || !canApproveJobs) {
      setNotice({ tone: 'error', text: 'Only supervisors and admins can approve jobs.' });
      return;
    }

    setApprovingJobId(jobId);
    setNotice(null);

    try {
      await approveOpsJob({ jobId }, sessionToken);
      await refreshLiveData();
      setNotice({ tone: 'success', text: 'Visit approved. Points now count toward salary.' });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setApprovingJobId(null);
    }
  };

  const submitReassignJob = async (jobId: string, assignedUserId: string | null) => {
    if (!sessionToken || !canAssignCleaners) {
      setNotice({ tone: 'error', text: 'Only supervisors and admins can reassign jobs.' });
      return;
    }

    setReassigningJobId(jobId);
    setNotice(null);

    try {
      await reassignOpsJob({ jobId, assignedUserId }, sessionToken);
      await refreshLiveData();
      setNotice({ tone: 'success', text: assignedUserId ? 'Städare tilldelad.' : 'Uppdrag avassignerat.' });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setReassigningJobId(null);
    }
  };

  const refreshAbsences = async (userId: string) => {
    if (!sessionToken || !canAssignCleaners) {
      setAbsences([]);
      return;
    }

    setIsAbsencesLoading(true);
    try {
      const next = await listCleanerAbsences({ userId }, sessionToken);
      setAbsences(next);
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setIsAbsencesLoading(false);
    }
  };

  const openAbsenceForm = (existing?: CleanerAbsenceRecord) => {
    if (existing) {
      setAbsenceDraft({
        id: existing.id,
        startsOn: existing.startsOn,
        endsOn: existing.endsOn,
        substituteUserId: existing.substituteUserId,
        reason: existing.reason ?? '',
      });
    } else {
      setAbsenceDraft({
        id: null,
        startsOn: '',
        endsOn: '',
        substituteUserId: null,
        reason: '',
      });
    }
  };

  const submitAbsence = async (userId: string) => {
    if (!sessionToken || !canAssignCleaners || !absenceDraft) return;

    if (!absenceDraft.startsOn || !absenceDraft.endsOn) {
      setNotice({ tone: 'error', text: 'Startdatum och slutdatum krävs.' });
      return;
    }

    if (absenceDraft.endsOn < absenceDraft.startsOn) {
      setNotice({ tone: 'error', text: 'Slutdatum kan inte vara före startdatum.' });
      return;
    }

    if (!absenceDraft.substituteUserId) {
      setNotice({ tone: 'error', text: 'Vikarie krävs. Välj en städare som täcker frånvaron.' });
      return;
    }

    setIsSavingAbsence(true);
    setNotice(null);

    try {
      await upsertCleanerAbsence(
        {
          id: absenceDraft.id ?? undefined,
          userId,
          startsOn: absenceDraft.startsOn,
          endsOn: absenceDraft.endsOn,
          substituteUserId: absenceDraft.substituteUserId,
          reason: absenceDraft.reason.trim() ? absenceDraft.reason.trim() : null,
        },
        sessionToken,
      );
      setAbsenceDraft(null);
      await refreshAbsences(userId);
      await refreshLiveData();
      setNotice({ tone: 'success', text: 'Frånvaro sparad.' });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setIsSavingAbsence(false);
    }
  };

  const submitDeleteAbsence = async (userId: string, absenceId: string) => {
    if (!sessionToken || !canAssignCleaners) return;
    if (!window.confirm('Ta bort denna frånvaroperiod? Tilldelningar av städare kan ändras.')) return;

    setDeletingAbsenceId(absenceId);
    setNotice(null);

    try {
      await deleteCleanerAbsence({ id: absenceId }, sessionToken);
      await refreshAbsences(userId);
      await refreshLiveData();
      setNotice({ tone: 'success', text: 'Frånvaro borttagen.' });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setDeletingAbsenceId(null);
    }
  };

  const createBlock = () => {
    const nextBlock = emptyBlockDraft(siteDraft.id ?? createDraftId('site'));
    const highestSortOrder = blockDrafts.reduce((max, block) => Math.max(max, block.sort_order), -1);
    nextBlock.sort_order = highestSortOrder + 1;
    setBlockDrafts((current) => [...current, nextBlock]);
    setSelectedBlockId(nextBlock.id ?? null);
    setNotice({ tone: 'warning', text: 'New block added to the draft. Save blocks to persist it.' });
  };

  const createExtraBlock = () => {
    const nextBlock = emptyExtraBlockDraft(siteDraft.id ?? createDraftId('site'));
    const highestSortOrder = blockDrafts.reduce((max, block) => Math.max(max, block.sort_order), -1);
    nextBlock.sort_order = highestSortOrder + 1;
    setBlockDrafts((current) => [...current, nextBlock]);
    setSelectedBlockId(nextBlock.id ?? null);
    setNotice({ tone: 'warning', text: 'New extra block added to the draft. Save blocks to persist it.' });
  };

  const createOneOffBlock = () => {
    const nextBlock = emptyOneOffBlockDraft(siteDraft.id ?? createDraftId('site'));
    const highestSortOrder = blockDrafts.reduce((max, block) => Math.max(max, block.sort_order), -1);
    nextBlock.sort_order = highestSortOrder + 1;
    setBlockDrafts((current) => [...current, nextBlock]);
    setSelectedBlockId(nextBlock.id ?? null);
    setNotice({ tone: 'warning', text: 'New one-off extra added to the draft. Save blocks to persist it.' });
  };

  const duplicateBlock = () => {
    if (!selectedBlock) {
      return;
    }

    const duplicate = normalizeBlockDraft({
      ...selectedBlock,
      id: createDraftId('block'),
      source_code: selectedBlock.source_code ? `${selectedBlock.source_code}-copy` : '',
      sort_order: selectedBlock.sort_order + 1,
    });

    setBlockDrafts((current) => [...current, duplicate]);
    setSelectedBlockId(duplicate.id ?? null);
    setNotice({ tone: 'warning', text: `Duplicated ${selectedBlock.source_code || 'block'} into the draft.` });
  };

  const deleteSelectedBlock = () => {
    if (!selectedBlock) {
      return;
    }

    const remaining = blockDrafts.filter((block) => block.id !== selectedBlock.id);
    setBlockDrafts(remaining);
    setSelectedBlockId(remaining[0]?.id ?? null);
    setNotice({ tone: 'warning', text: `Removed ${selectedBlock.source_code || 'block'} from the draft. Save blocks to commit the deletion.` });
  };

  const submitGeneralSave = async () => {
    if (!sessionToken) {
      setNotice({ tone: 'error', text: 'Missing auth session.' });
      return;
    }

    if (siteValidationError) {
      setNotice({ tone: 'error', text: siteValidationError });
      return;
    }

    setIsSavingSite(true);
    setNotice(null);

    try {
      const result = await saveOpsSite(draftToSitePayload(normalizedSiteDraft), sessionToken);
      await refreshLiveData();
      navigateToRoute({ page: 'site', siteId: result.siteId, tab: 'general' });
      setNotice({
        tone: 'success',
        text: `${normalizedSiteDraft.name} saved.`,
      });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setIsSavingSite(false);
    }
  };

  const submitBlocksSave = async () => {
    if (!sessionToken) {
      setNotice({ tone: 'error', text: 'Missing auth session.' });
      return;
    }

    if (siteValidationError) {
      setNotice({ tone: 'error', text: siteValidationError });
      return;
    }

    if (blockValidationError) {
      setNotice({ tone: 'error', text: blockValidationError });
      return;
    }

    setIsSavingBlocks(true);
    setNotice(null);

    try {
      const result = await saveOpsBundle(
        {
          site: draftToSitePayload(normalizedSiteDraft),
          blocks: normalizedBlockDrafts.map((block) => ({
            ...block,
            site_id: normalizedSiteDraft.id ?? block.site_id,
          })),
          replaceBlocks: true,
        },
        sessionToken,
      );

      await refreshLiveData();
      navigateToRoute({ page: 'site', siteId: result.siteId, tab: 'blocks' });
      setNotice({
        tone: 'success',
        text: `${normalizedSiteDraft.name || 'BRF'} saved with ${result.blockCount} block${result.blockCount === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    } finally {
      setIsSavingBlocks(false);
    }
  };

  const submitRegenerateManually = async () => {
    if (!selectedSite || !sessionToken) {
      setNotice({ tone: 'error', text: 'Missing auth session or site.' });
      return;
    }

    setNotice(null);

    try {
      await regenerateOpsJobs({ siteId: selectedSite.id, year, month }, sessionToken);
      await refreshLiveData();
      setNotice({
        tone: 'success',
        text: `Manuell regenerering klar för ${selectedSite.name} ${formatMonthLabel(month, language)} ${year}.`,
      });
    } catch (error) {
      setNotice({ tone: 'error', text: getErrorMessage(error) });
    }
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-4">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">Loading</h1>
          <p className="text-sm text-muted-foreground">Loading live sites, blocks, runs, and jobs.</p>
        </header>
        <Card>
          <CardContent className="flex items-center gap-3 pt-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Syncing live data
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderBrfList = () => (
    <div className="space-y-4">
      <div className="cmd-panel cmd-panel-inner rounded p-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="cmd-label">BRF:ER — PORTFÖLJ</span>
          <div className="flex items-center gap-3">
            <span className="cmd-label text-[#8A6A47] font-mono tabular-nums">
              {filteredSites.length}/{sites.length} OBJEKT
            </span>
            <button
              type="button"
              onClick={() => navigateToRoute({ page: 'site', siteId: NEW_SITE_ID, tab: 'general' })}
              className="cmd-label inline-flex items-center gap-1 rounded px-2 py-1 text-[#8A6A47] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331]"
              style={{ border: '1px solid rgba(7,51,49,0.12)' }}
            >
              <Plus className="h-3 w-3" />
              NY BRF
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            value={siteSearch}
            onChange={(event) => setSiteSearch(event.target.value)}
            placeholder="Sök namn, adress eller kundnr"
            className="flex-1 min-w-[200px] rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] placeholder:text-[#607C6E] focus:outline-none focus-visible:border-[#8A6A47]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          />
          <select
            value={siteStatusFilter}
            onChange={(event) => setSiteStatusFilter(event.target.value as 'all' | Site['status'])}
            className="rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] focus:outline-none focus-visible:border-[#8A6A47]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          >
            <option value="all">ALLA STATUS</option>
            <option value="active">AKTIV</option>
            <option value="paused">PAUSAD</option>
            <option value="archived">ARKIVERAD</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto cmd-scroll">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10">BRF</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-24">STATUS</th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-16">BLOCK</th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">
                  {formatMonthLabel(month, language)} BESÖK
                </th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-40">AVVIKELSER</th>
                <th className="border-b border-[#073331]/10 w-6" />
              </tr>
            </thead>
            <tbody>
              {filteredSites.map((site) => {
                const monthStats = monthJobStatsBySite.get(site.id);
                const blockCount = workBlocksBySite.get(site.id)?.length ?? 0;
                const flags = avvikelserMap.get(site.id) ?? [];

                return (
                  <tr
                    key={site.id}
                    onClick={() => navigateToRoute({ page: 'site', siteId: site.id, tab: 'general' })}
                    className="border-b border-[#073331]/10 cursor-pointer hover:bg-[#073331]/[0.04] transition-colors group align-middle"
                    style={{ height: 36 }}
                  >
                    <td className="py-1 pr-2">
                      <div className="font-mono text-xs text-[#2E4640] group-hover:text-[#073331] transition-colors leading-tight">
                        {site.name}
                      </div>
                      {site.address ? (
                        <div className="font-mono text-[10px] text-[#607C6E] leading-tight truncate max-w-[220px]">
                          {site.address}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-1 px-2">
                      <span className="cmd-label text-[#607C6E] group-hover:text-[#2E4640] transition-colors">
                        {site.status}
                      </span>
                    </td>
                    <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">
                      {blockCount}
                    </td>
                    <td className="py-1 px-2 text-right">
                      <div className="font-mono tabular-nums text-xs text-[#073331] leading-tight">
                        {monthStats?.total ?? 0}
                      </div>
                      <div className="font-mono tabular-nums text-[10px] text-[#607C6E] leading-tight">
                        {(monthStats?.points ?? 0).toFixed(2)} p
                      </div>
                    </td>
                    <td className="py-1 px-2 text-right">
                      {flags.length > 0 ? (
                        <AvvikelsePills siteId={site.id} flags={flags} language={language} />
                      ) : (
                        <span className="font-mono text-xs text-[#607C6E]">—</span>
                      )}
                    </td>
                    <td className="py-1 pl-1 text-right">
                      <ChevronRight className="h-3.5 w-3.5 text-[#607C6E] group-hover:text-[#8A6A47] transition-colors inline-block" />
                    </td>
                  </tr>
                );
              })}

              {filteredSites.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center font-mono text-xs text-[#607C6E] py-4">
                    Inga BRF:er matchar filtret.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const roleChipStyle = (role: PlanningUserRecord['role']): React.CSSProperties =>
    role === 'admin'
      ? { background: 'rgba(160,123,82,0.12)', border: '1px solid rgba(138,106,71,0.4)', color: '#6E5438' }
      : role === 'supervisor'
        ? { background: 'rgba(27,127,77,0.10)', border: '1px solid rgba(27,127,77,0.35)', color: '#1B7F4D' }
        : { background: 'rgba(7,51,49,0.04)', border: '1px solid rgba(7,51,49,0.12)', color: '#607C6E' };

  const renderTeamWorkspace = () => (
    <div className="space-y-4">
      <div className="cmd-panel cmd-panel-inner rounded p-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="cmd-label">TEAM — PERSONAL</span>
          <div className="flex items-center gap-3">
            <span className="cmd-label text-[#8A6A47] font-mono tabular-nums">
              {filteredTeamMemberSummaries.length}/{teamMemberSummaries.length} PERSONER
            </span>
            <button
              type="button"
              onClick={() => void refreshUsers()}
              disabled={isUsersLoading}
              className="cmd-label inline-flex items-center gap-1 rounded px-2 py-1 text-[#607C6E] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331] disabled:opacity-40"
              style={{ border: '1px solid rgba(7,51,49,0.12)' }}
            >
              {isUsersLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              UPPDATERA
            </button>
            <button
              type="button"
              onClick={() => setIsCreateUserDialogOpen(true)}
              className="cmd-label inline-flex items-center gap-1 rounded px-2 py-1 text-[#8A6A47] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331]"
              style={{ border: '1px solid rgba(7,51,49,0.12)' }}
            >
              <Plus className="h-3 w-3" />
              NY MEDLEM
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            value={teamSearch}
            onChange={(event) => setTeamSearch(event.target.value)}
            placeholder="Sök namn, e-post eller roll"
            className="flex-1 min-w-[200px] rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] placeholder:text-[#607C6E] focus:outline-none focus-visible:border-[#8A6A47]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          />
          <select
            value={teamRoleFilter}
            onChange={(event) => setTeamRoleFilter(event.target.value as 'all' | PlanningUserRecord['role'])}
            className="rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] focus:outline-none focus-visible:border-[#8A6A47]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          >
            <option value="all">ALLA ROLLER</option>
            <option value="cleaner">STÄDARE</option>
            <option value="supervisor">STÄDLEDARE</option>
            <option value="admin">LEDNING</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto cmd-scroll">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10">MEDLEM</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-32">ROLL</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-24">STATUS</th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-48">
                  {formatMonthLabel(month, language)} AKTIVITET
                </th>
                <th className="border-b border-[#073331]/10 w-6" />
              </tr>
            </thead>
            <tbody>
              {filteredTeamMemberSummaries.map((summary) => (
                <tr
                  key={summary.user.id}
                  onClick={() => navigateToRoute({
                    page: 'team-member',
                    userId: summary.user.id,
                    tab: summary.user.role === 'cleaner' ? 'points' : 'profile',
                  })}
                  className="border-b border-[#073331]/10 cursor-pointer hover:bg-[#073331]/[0.04] transition-colors group align-middle"
                  style={{ height: 36 }}
                >
                  <td className="py-1 pr-2">
                    <div className="font-mono text-xs text-[#2E4640] group-hover:text-[#073331] transition-colors leading-tight">
                      {getUserDisplayName(summary.user)}
                    </div>
                    <div className="font-mono text-[10px] text-[#607C6E] leading-tight truncate max-w-[240px]">
                      {summary.user.email}
                    </div>
                  </td>
                  <td className="py-1 px-2">
                    <span
                      className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide"
                      style={roleChipStyle(summary.user.role)}
                    >
                      {formatRoleLabel(summary.user.role)}
                    </span>
                  </td>
                  <td className="py-1 px-2">
                    <span
                      className="cmd-label transition-colors"
                      style={{ color: summary.user.active ? '#1B7F4D' : '#607C6E' }}
                    >
                      {summary.user.active ? 'AKTIV' : 'INAKTIV'}
                    </span>
                  </td>
                  <td className="py-1 px-2 text-right">
                    {summary.user.role === 'cleaner' ? (
                      <>
                        <div className="font-mono tabular-nums text-xs text-[#073331] leading-tight">
                          {summary.month.approvedPoints.toFixed(2)} p
                        </div>
                        <div className="font-mono text-[10px] text-[#607C6E] leading-tight">godkänt</div>
                      </>
                    ) : summary.month.approvedVisits > 0 ? (
                      <>
                        <div className="font-mono tabular-nums text-xs text-[#073331] leading-tight">
                          {summary.month.approvedVisits}
                        </div>
                        <div className="font-mono text-[10px] text-[#607C6E] leading-tight">godkännanden</div>
                      </>
                    ) : (
                      <span className="font-mono text-xs text-[#607C6E]">—</span>
                    )}
                  </td>
                  <td className="py-1 pl-1 text-right">
                    <ChevronRight className="h-3.5 w-3.5 text-[#607C6E] group-hover:text-[#8A6A47] transition-colors inline-block" />
                  </td>
                </tr>
              ))}

              {filteredTeamMemberSummaries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center font-mono text-xs text-[#607C6E] py-4">
                    Inga teammedlemmar matchar filtret.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={isCreateUserDialogOpen} onOpenChange={setIsCreateUserDialogOpen}>
        <DialogContent className="max-w-xl border-[#073331]/12 bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#073331]">Add team member</DialogTitle>
            <DialogDescription>Create a cleaner, supervisor, or admin account from one modal.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Email</Label>
              <Input
                value={createUserDraft.email}
                onChange={(event) => setCreateUserDraft((current) => ({ ...current, email: event.target.value }))}
                placeholder="cleaner@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={createUserDraft.password}
                onChange={(event) => setCreateUserDraft((current) => ({ ...current, password: event.target.value }))}
                placeholder="Minimum 8 chars"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={createUserDraft.role}
                onValueChange={(value) => setCreateUserDraft((current) => ({ ...current, role: value as PlanningUserRecord['role'] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cleaner">Cleaner</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Full name</Label>
              <Input
                value={createUserDraft.full_name}
                onChange={(event) => setCreateUserDraft((current) => ({ ...current, full_name: event.target.value }))}
                placeholder="Team member name"
              />
            </div>
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <Checkbox
                checked={createUserDraft.active}
                onCheckedChange={(checked) => setCreateUserDraft((current) => ({ ...current, active: Boolean(checked) }))}
              />
              Active account
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsCreateUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void submitCreateUser()}
              disabled={isCreatingUser}
              className="bg-[#8A6A47] text-[#F0EEE6] hover:bg-[#6E5438]"
            >
              {isCreatingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderTeamMemberDetail = () => {
    if (route.page !== 'team-member' || !selectedTeamMember || !selectedTeamSummary) {
      return null;
    }

    const profileDraft = userDrafts[selectedTeamMember.id] ?? {
      full_name: selectedTeamMember.full_name ?? '',
      role: selectedTeamMember.role,
      active: selectedTeamMember.active,
    };
    const baseTabs: TeamMemberDetailTab[] = selectedTeamMember.role === 'cleaner'
      ? ['points', 'activity', 'profile']
      : selectedTeamMember.role === 'admin'
        ? ['profile', 'activity', 'admin']
        : ['profile', 'activity'];
    const allowedTabs: TeamMemberDetailTab[] = canAssignCleaners
      ? [...baseTabs, 'vacations']
      : baseTabs;
    const activeTab = allowedTabs.includes(route.tab) ? route.tab : allowedTabs[0];
    const monthLabel = `${formatMonthLabel(month, language)} ${year}`;
    const recentVisits = [...selectedTeamMemberRows]
      .sort((a, b) => b.scheduledFor.localeCompare(a.scheduledFor) || b.id.localeCompare(a.id))
      .slice(0, 12);
    const monthLedgerRows = [...selectedTeamMemberMonthRows]
      .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor) || a.siteName.localeCompare(b.siteName));
    const recentApprovals = [...selectedApprovals]
      .sort((a, b) => (b.approvedAt ?? '').localeCompare(a.approvedAt ?? '') || b.id.localeCompare(a.id))
      .slice(0, 12);
    const monthApprovalPoints = roundPoints(selectedMonthApprovals.reduce((sum, row) => sum + row.totalPoints, 0));
    const lifetimeApprovalPoints = roundPoints(selectedApprovals.reduce((sum, row) => sum + row.totalPoints, 0));
    const lastApprovalAt = recentApprovals[0]?.approvedAt ?? null;

    const tabLabel = (tab: TeamMemberDetailTab) =>
      tab === 'profile' ? 'PROFIL'
      : tab === 'points' ? 'POÄNG'
      : tab === 'activity' ? 'AKTIVITET'
      : tab === 'vacations' ? 'SEMESTER'
      : 'ADMIN';

    return (
      <div className="space-y-4">
        <div className="cmd-panel cmd-panel-inner rounded p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => navigateToRoute({ page: 'team' })}
                className="cmd-label inline-flex items-center gap-1 text-[#8A6A47] transition-colors hover:text-[#6E5438]"
              >
                <ArrowLeft className="h-3 w-3" />
                TILLBAKA TILL TEAM
              </button>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold text-[#073331]">{getUserDisplayName(selectedTeamMember)}</h1>
                <span
                  className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide"
                  style={roleChipStyle(selectedTeamMember.role)}
                >
                  {formatRoleLabel(selectedTeamMember.role)}
                </span>
                {!selectedTeamMember.active ? (
                  <span className="cmd-label" style={{ color: '#607C6E' }}>INAKTIV</span>
                ) : null}
              </div>
              <p className="font-mono text-xs text-[#607C6E]">{selectedTeamMember.email}</p>
            </div>

            <MonthScopeControls
              language={language}
              month={month}
              year={year}
              onMonthChange={setMonth}
              onYearChange={setYear}
              onRefresh={() => {
                void refreshLiveData();
                void refreshUsers();
              }}
              isRefreshing={isRefreshing || isUsersLoading}
              syncedAt={data?.syncedAt ?? null}
              refreshLabel="Refresh"
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-6 border-b border-[#073331]/10">
            {allowedTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => navigateToRoute({ page: 'team-member', userId: selectedTeamMember.id, tab })}
                className={cn(
                  'cmd-label -mb-px h-9 border-b-2 px-0.5 transition-colors',
                  activeTab === tab
                    ? 'border-[#8A6A47] text-[#073331]'
                    : 'border-transparent text-[#607C6E] hover:text-[#073331]',
                )}
              >
                {tabLabel(tab)}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'points' && selectedTeamMember.role === 'cleaner' ? (
          <div className="space-y-4">
            <div className="cmd-panel cmd-panel-inner rounded p-4">
              <span className="cmd-label">POÄNGUTVECKLING</span>
              <p className="mt-1 font-mono text-[11px] text-[#607C6E]">
                Godkända besök räknas mot lön. Slutförda besök väntar tills de godkänts.
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">LÖNEPOÄNG TOTALT</div>
                  <div className="mt-1.5 font-mono tabular-nums text-2xl font-semibold text-[#073331]">{selectedTeamSummary.lifetime.approvedPoints.toFixed(2)}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">LÖNEPOÄNG {monthLabel}</div>
                  <div className="mt-1.5 font-mono tabular-nums text-2xl font-semibold text-[#073331]">{selectedTeamSummary.month.approvedPoints.toFixed(2)}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">VÄNTAR GODKÄNNANDE</div>
                  <div className="mt-1.5 font-mono tabular-nums text-2xl font-semibold text-[#B97E1F]">{selectedTeamSummary.month.pendingApprovalPoints.toFixed(2)}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">PLANERAT {monthLabel}</div>
                  <div className="mt-1.5 font-mono tabular-nums text-2xl font-semibold text-[#073331]">{selectedTeamSummary.month.scheduledPoints.toFixed(2)}</div>
                </div>
              </div>

              <div className="mt-3 grid gap-3 md:grid-cols-4">
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">GODKÄNDA BESÖK</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedTeamSummary.month.approvedVisits}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">SLUTFÖRT, VÄNTAR</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedTeamSummary.month.pendingApprovalVisits}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">BRF:ER TÄCKTA</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedTeamSummary.siteCount}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">SENASTE BESÖK</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedTeamSummary.lastVisit ?? '—'}</div>
                </div>
              </div>
            </div>

            <div className="cmd-panel cmd-panel-inner rounded p-4">
              <span className="cmd-label">{monthLabel} — HUVUDBOK</span>
              <p className="mt-1 mb-3 font-mono text-[11px] text-[#607C6E]">
                Varje besök visar totala poäng och om de redan är krediterade.
              </p>
              <div className="overflow-x-auto cmd-scroll">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10 w-28">DATUM</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">BRF</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">STATUS</th>
                      <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-24">POÄNG</th>
                      <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">KREDITERAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthLedgerRows.map((row) => (
                      <tr key={row.id} className="border-b border-[#073331]/10" style={{ height: 34 }}>
                        <td className="py-1 pr-2 font-mono text-xs text-[#2E4640]">{row.scheduledFor}</td>
                        <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">{row.siteName}</td>
                        <td className="py-1 px-2">
                          <span className="cmd-label" style={{ color: statusInk(row.status) }}>{row.status}</span>
                        </td>
                        <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{row.totalPoints.toFixed(2)}</td>
                        <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">
                          {row.status === 'approved' ? row.totalPoints.toFixed(2) : '—'}
                        </td>
                      </tr>
                    ))}

                    {monthLedgerRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center font-mono text-xs text-[#607C6E] py-4">
                          Inga besök för denna medlem i {monthLabel}.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>

            <CleanerPointsPanel userId={selectedTeamMember.id} />
          </div>
        ) : null}

        {activeTab === 'activity' ? (
          selectedTeamMember.role === 'cleaner' ? (
            <div className="cmd-panel cmd-panel-inner rounded p-4">
              <span className="cmd-label">STÄDARE — AKTIVITET</span>
              <p className="mt-1 mb-3 font-mono text-[11px] text-[#607C6E]">
                Senaste besök och statusmix över hela uppdragshistoriken.
              </p>
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">BESÖK TOTALT</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedTeamSummary.lifetime.visits}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">SLUTFÖRDA POÄNG</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedTeamSummary.lifetime.completedPoints.toFixed(2)}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">NÄSTA BESÖK</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedTeamSummary.nextVisit ?? '—'}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">AVBOKADE BESÖK</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#C24138]">{selectedTeamSummary.lifetime.cancelledVisits}</div>
                </div>
              </div>

              <div className="mt-3 overflow-x-auto cmd-scroll">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10 w-28">DATUM</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">BRF</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">STATUS</th>
                      <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-24">POÄNG</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentVisits.map((row) => (
                      <tr key={row.id} className="border-b border-[#073331]/10" style={{ height: 34 }}>
                        <td className="py-1 pr-2 font-mono text-xs text-[#2E4640]">{row.scheduledFor}</td>
                        <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">{row.siteName}</td>
                        <td className="py-1 px-2">
                          <span className="cmd-label" style={{ color: statusInk(row.status) }}>{row.status}</span>
                        </td>
                        <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{row.totalPoints.toFixed(2)}</td>
                      </tr>
                    ))}

                    {recentVisits.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center font-mono text-xs text-[#607C6E] py-4">
                          Ingen besökshistorik ännu.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="cmd-panel cmd-panel-inner rounded p-4">
              <span className="cmd-label">
                {selectedTeamMember.role === 'admin' ? 'LEDNING — AKTIVITET' : 'STÄDLEDARE — AKTIVITET'}
              </span>
              <p className="mt-1 mb-3 font-mono text-[11px] text-[#607C6E]">
                Godkännandehistorik baserad på besök denna användare signerat.
              </p>
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">GODKÄNNANDEN {monthLabel}</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedMonthApprovals.length}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">GODKÄNDA POÄNG {monthLabel}</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{monthApprovalPoints.toFixed(2)}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">GODKÄNNANDEN TOTALT</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{selectedApprovals.length}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">GODKÄNDA POÄNG TOTALT</div>
                  <div className="mt-1 font-mono tabular-nums text-base font-semibold text-[#073331]">{lifetimeApprovalPoints.toFixed(2)}</div>
                </div>
              </div>

              <div className="mt-3 rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                <span className="cmd-label">SENASTE GODKÄNNANDE</span>
                <span className="ml-2 font-mono text-xs text-[#073331]">{lastApprovalAt ? lastApprovalAt.slice(0, 19).replace('T', ' ') : '—'}</span>
              </div>

              <div className="mt-3 overflow-x-auto cmd-scroll">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10 w-44">GODKÄNT</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">STÄDARE</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">BRF</th>
                      <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-24">POÄNG</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentApprovals.map((row) => (
                      <tr key={row.id} className="border-b border-[#073331]/10" style={{ height: 34 }}>
                        <td className="py-1 pr-2 font-mono text-xs text-[#2E4640]">{row.approvedAt ? row.approvedAt.slice(0, 19).replace('T', ' ') : '—'}</td>
                        <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">{row.assignedUserId ? getUserDisplayName(usersById.get(row.assignedUserId) ?? null) : '—'}</td>
                        <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">{row.siteName}</td>
                        <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{row.totalPoints.toFixed(2)}</td>
                      </tr>
                    ))}

                    {recentApprovals.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center font-mono text-xs text-[#607C6E] py-4">
                          Inga godkännanden registrerade för denna användare ännu.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : null}

        {activeTab === 'profile' ? (
          <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-4">
            <div>
              <span className="cmd-label">MEDLEMSPROFIL</span>
              <p className="mt-1 font-mono text-[11px] text-[#607C6E]">
                Uppdatera medlemmens identitet, roll och kontostatus.
              </p>
            </div>
            <div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={selectedTeamMember.email} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Full name</Label>
                  <Input
                    value={profileDraft.full_name}
                    onChange={(event) => updateUserDraft(selectedTeamMember.id, { full_name: event.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={profileDraft.role}
                    onValueChange={(value) => updateUserDraft(selectedTeamMember.id, { role: value as PlanningUserRecord['role'] })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cleaner">Cleaner</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Account</Label>
                  <label className="flex h-10 items-center gap-2 rounded-md border px-3 text-sm">
                    <Checkbox
                      checked={profileDraft.active}
                      onCheckedChange={(checked) => updateUserDraft(selectedTeamMember.id, { active: Boolean(checked) })}
                    />
                    Active account
                  </label>
                </div>
              </div>

              <div className="my-4 border-t border-[#073331]/10" />

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">SKAPAD</div>
                  <div className="mt-1 font-mono text-xs text-[#073331]">{selectedTeamMember.created_at.slice(0, 19).replace('T', ' ')}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">UPPDATERAD</div>
                  <div className="mt-1 font-mono text-xs text-[#073331]">{selectedTeamMember.updated_at.slice(0, 19).replace('T', ' ')}</div>
                </div>
                <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="cmd-label">TILLDELADE BRF:ER</div>
                  <div className="mt-1 font-mono tabular-nums text-xs text-[#073331]">{selectedTeamSummary.siteCount}</div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  type="button"
                  onClick={() => void submitUserUpdate(selectedTeamMember.id)}
                  disabled={savingUserId === selectedTeamMember.id}
                  className="bg-[#8A6A47] text-[#F0EEE6] hover:bg-[#6E5438]"
                >
                  {savingUserId === selectedTeamMember.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save member
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === 'admin' && selectedTeamMember.role === 'admin' ? (
          <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-3">
            <div>
              <span className="cmd-label">ADMIN — ÅTKOMST</span>
              <p className="mt-1 font-mono text-[11px] text-[#607C6E]">
                Denna flik håller admin-omfånget explicit medan resten av appen växer.
              </p>
            </div>
            <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
              <div className="cmd-label">NUVARANDE ADMIN-ANSVAR</div>
              <div className="mt-2 font-mono text-xs text-[#2E4640]">
                Skapa teammedlemmar, hantera roller, öppna valfri medlemssida, godkänna arbete och konfigurera städartilldelning i blockregler.
              </div>
            </div>
            <div className="rounded px-3 py-2.5" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
              <div className="cmd-label">GODKÄNNANDEN DENNA MÅNAD</div>
              <div className="mt-2 font-mono text-xs text-[#2E4640]">
                {selectedMonthApprovals.length} besök godkända för {monthApprovalPoints.toFixed(2)} poäng i {monthLabel}.
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === 'vacations' && canAssignCleaners ? (
          <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <span className="cmd-label">SEMESTER OCH FRÅNVARO</span>
                <p className="mt-1 max-w-2xl font-mono text-[11px] text-[#607C6E]">
                  Lägg till frånvaroperioder och tilldela ersättare. Framtida uppdrag som överlappar perioden får
                  den valda ersättaren automatiskt.
                </p>
              </div>
              <button
                type="button"
                onClick={() => openAbsenceForm()}
                disabled={isSavingAbsence || absenceDraft !== null}
                className="cmd-label inline-flex items-center gap-1 rounded px-2 py-1 text-[#8A6A47] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331] disabled:opacity-40"
                style={{ border: '1px solid rgba(7,51,49,0.12)' }}
              >
                <Plus className="h-3 w-3" />
                LÄGG TILL FRÅNVARO
              </button>
            </div>
            <div className="space-y-4">
              {absenceDraft ? (
                <div className="space-y-4 rounded p-4" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Startdatum</Label>
                      <Input
                        type="date"
                        value={absenceDraft.startsOn}
                        onChange={(event) =>
                          setAbsenceDraft((prev) => (prev ? { ...prev, startsOn: event.target.value } : prev))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Slutdatum</Label>
                      <Input
                        type="date"
                        value={absenceDraft.endsOn}
                        onChange={(event) =>
                          setAbsenceDraft((prev) => (prev ? { ...prev, endsOn: event.target.value } : prev))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Ersättare <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={absenceDraft.substituteUserId ?? ''}
                        onValueChange={(value) =>
                          setAbsenceDraft((prev) => (prev ? { ...prev, substituteUserId: value } : prev))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Välj ersättare (krävs)" />
                        </SelectTrigger>
                        <SelectContent>
                          {cleanerUsers
                            .filter((user) => user.id !== selectedTeamMember.id)
                            .map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {getUserDisplayName(user)}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Frånvaro kräver en ersättare. Om ingen kan täcka, avboka jobben istället.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Anledning (valfritt)</Label>
                      <Input
                        type="text"
                        value={absenceDraft.reason}
                        onChange={(event) =>
                          setAbsenceDraft((prev) => (prev ? { ...prev, reason: event.target.value } : prev))
                        }
                        placeholder="T.ex. semester, sjukdom"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAbsenceDraft(null)}
                      disabled={isSavingAbsence}
                    >
                      Avbryt
                    </Button>
                    <Button
                      type="button"
                      onClick={() => void submitAbsence(selectedTeamMember.id)}
                      disabled={isSavingAbsence}
                      className="bg-[#8A6A47] text-[#F0EEE6] hover:bg-[#6E5438]"
                    >
                      {isSavingAbsence ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Spara
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="overflow-x-auto cmd-scroll">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10 w-28">START</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">SLUT</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">ERSÄTTARE</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">ANLEDNING</th>
                      <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-40">ÅTGÄRDER</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isAbsencesLoading ? (
                      <tr>
                        <td colSpan={5} className="text-center font-mono text-xs text-[#607C6E] py-4">
                          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                          Hämtar frånvaroperioder…
                        </td>
                      </tr>
                    ) : absences.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center font-mono text-xs text-[#607C6E] py-4">
                          Inga frånvaroperioder registrerade.
                        </td>
                      </tr>
                    ) : (
                      absences.map((absence) => {
                        const substitute = absence.substituteUserId
                          ? (usersById.get(absence.substituteUserId) ?? null)
                          : null;
                        return (
                          <tr key={absence.id} className="border-b border-[#073331]/10 align-middle" style={{ height: 36 }}>
                            <td className="py-1 pr-2 font-mono text-xs text-[#073331]">{absence.startsOn}</td>
                            <td className="py-1 px-2 font-mono text-xs text-[#073331]">{absence.endsOn}</td>
                            <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">
                              {substitute ? getUserDisplayName(substitute) : <span className="text-[#607C6E]">Ingen</span>}
                            </td>
                            <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">
                              {absence.reason ? absence.reason : <span className="text-[#607C6E]">—</span>}
                            </td>
                            <td className="py-1 px-2">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => openAbsenceForm(absence)}
                                  disabled={isSavingAbsence || deletingAbsenceId === absence.id}
                                  className="cmd-label inline-flex items-center gap-1 rounded px-2 py-1 text-[#607C6E] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331] disabled:opacity-40"
                                  style={{ border: '1px solid rgba(7,51,49,0.12)' }}
                                >
                                  REDIGERA
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void submitDeleteAbsence(selectedTeamMember.id, absence.id)}
                                  disabled={deletingAbsenceId === absence.id}
                                  className="cmd-label inline-flex items-center gap-1 rounded px-2 py-1 text-[#C24138] transition-colors hover:bg-[#C24138]/[0.08] disabled:opacity-40"
                                  style={{ border: '1px solid rgba(194,65,56,0.3)' }}
                                >
                                  {deletingAbsenceId === absence.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3 w-3" />
                                  )}
                                  TA BORT
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderGeneralCalendar = () => {
    const goToMonth = (delta: number) => {
      const total = (year * 12 + (month - 1)) + delta;
      setYear(Math.floor(total / 12));
      setMonth((total % 12) + 1);
    };

    return (
    <div className="space-y-4">
      <div className="cmd-panel cmd-panel-inner rounded p-4">
        {/* Header + month nav */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="cmd-label mr-1">GENERELL KALENDER</span>
          <div className="order-last ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToMonth(-1)}
              aria-label="Föregående månad"
              className="font-mono text-base leading-none rounded px-2 py-1 text-[#607C6E] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331]"
              style={{ border: '1px solid rgba(7,51,49,0.12)' }}
            >
              ‹
            </button>
            <span className="cmd-label text-[#073331] font-mono tabular-nums min-w-[120px] text-center">
              {formatMonthLabel(month, language).toUpperCase()} {year}
            </span>
            <button
              type="button"
              onClick={() => goToMonth(1)}
              aria-label="Nästa månad"
              className="font-mono text-base leading-none rounded px-2 py-1 text-[#607C6E] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331]"
              style={{ border: '1px solid rgba(7,51,49,0.12)' }}
            >
              ›
            </button>
          </div>
          <select
            value={calendarSiteFilter}
            onChange={(event) => setCalendarSiteFilter(event.target.value)}
            className="rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] focus:outline-none focus-visible:border-[#8A6A47] max-w-[200px]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          >
            <option value={ALL_SITES}>ALLA BRF:ER</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
          <select
            value={calendarCleanerFilter}
            onChange={(event) => setCalendarCleanerFilter(event.target.value)}
            className="rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] focus:outline-none focus-visible:border-[#8A6A47] max-w-[200px]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          >
            <option value={ALL_CLEANERS}>ALLA STÄDARE</option>
            <option value={UNASSIGNED_CLEANER}>SAKNAR TILLDELNING</option>
            {cleanerUsers.map((user) => (
              <option key={user.id} value={user.id}>{getUserDisplayName(user)}</option>
            ))}
          </select>
          <select
            value={calendarStatusFilter}
            onChange={(event) => setCalendarStatusFilter(event.target.value)}
            className="rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] focus:outline-none focus-visible:border-[#8A6A47]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          >
            <option value={ALL_JOB_STATUSES}>ALLA STATUS</option>
            <option value="assigned">ASSIGNED</option>
            <option value="completed">COMPLETED</option>
            <option value="cancelled">CANCELLED</option>
            <option value="approved">APPROVED</option>
          </select>
          {(calendarSiteFilter !== ALL_SITES || calendarStatusFilter !== ALL_JOB_STATUSES || calendarCleanerFilter !== ALL_CLEANERS) && (
            <button
              type="button"
              onClick={() => {
                setCalendarSiteFilter(ALL_SITES);
                setCalendarStatusFilter(ALL_JOB_STATUSES);
                setCalendarCleanerFilter(ALL_CLEANERS);
              }}
              className="cmd-label rounded px-2 py-1.5 text-[#8A6A47] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331]"
              style={{ border: '1px solid rgba(7,51,49,0.12)' }}
            >
              RENSA ALLA
            </button>
          )}
        </div>
        <div className="overflow-auto cmd-scroll mt-3 max-h-[74vh]">
          <MonthGridCalendar
            language={language}
            year={year}
            month={month}
            itemsByDate={generalCalendarItems}
            showSiteName
          />
        </div>
      </div>

      <div className="cmd-panel cmd-panel-inner rounded p-4">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <span className="cmd-label">BESÖKSRADER</span>
            <p className="mt-1 font-mono text-[11px] text-[#607C6E]">Genererade besök för en BRF i taget.</p>
          </div>
          <div className="w-full max-w-sm">
              <Label>BRF</Label>
              <Popover open={isVisitRowsSitePickerOpen} onOpenChange={setIsVisitRowsSitePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={isVisitRowsSitePickerOpen}
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">{selectedVisitRowsSite?.name ?? 'Select BRF'}</span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="end">
                  <Command>
                    <CommandInput placeholder="Search BRF..." />
                    <CommandList>
                      <CommandEmpty>No BRF found.</CommandEmpty>
                      <CommandGroup>
                        {visitRowSiteOptions.map((site) => (
                          <CommandItem
                            key={site.id}
                            value={`${site.name} ${site.address} ${site.customer_number ?? ''}`}
                            onSelect={() => {
                              setVisitRowsSiteFilter(site.id);
                              setIsVisitRowsSitePickerOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'h-4 w-4',
                                visitRowsSiteFilter === site.id ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <span className="truncate">{site.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        <div className="overflow-x-auto cmd-scroll">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10 w-28">DATUM</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">BRF</th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-20">BLOCK</th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-20">POÄNG</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">STATUS</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">STÄDARE</th>
              </tr>
            </thead>
            <tbody>
              {filteredVisitRows.map((row) => (
                <tr key={row.id} className="border-b border-[#073331]/10 align-middle" style={{ height: 34 }}>
                  <td className="py-1 pr-2 font-mono text-xs text-[#073331]">{row.scheduledFor}</td>
                  <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">{row.siteName}</td>
                  <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{row.blocks.length}</td>
                  <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{row.totalPoints.toFixed(2)}</td>
                  <td className="py-1 px-2">
                    <span className="cmd-label" style={{ color: statusInk(row.status) }}>{row.status}</span>
                  </td>
                  <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">{row.assignedUserId ? (usersById.get(row.assignedUserId)?.full_name ?? usersById.get(row.assignedUserId)?.email ?? row.assignedUserId) : '—'}</td>
                </tr>
              ))}

              {filteredVisitRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center font-mono text-xs text-[#607C6E] py-4">
                    {selectedVisitRowsSite
                      ? 'Inga genererade besök matchar denna BRF och status.'
                      : 'Inga genererade BRF:er tillgängliga för denna månad.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    );
  };

  const renderSiteDetail = () => {
    const detailTitle = selectedSite?.name ?? normalizedSiteDraft.name ?? 'New BRF';
    const isReadonlyTab = isNewSite && route.tab !== 'general' && route.tab !== 'blocks' && route.tab !== 'extras';
    // Drafts have no id in the map → no strip. Show every flag (never the count pill) here.
    const detailFlags = selectedSite ? orderFlags(avvikelserMap.get(selectedSite.id) ?? []) : [];

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#073331]/10 pb-4">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => navigateToRoute({ page: 'brfs' })}
              className="cmd-label inline-flex items-center gap-1 px-0 text-[#8A6A47] transition-colors hover:text-[#6E5438]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {language === 'sv' ? 'TILLBAKA TILL BRF:ER' : 'BACK TO BRFS'}
            </button>
            <div>
              <h2 className="text-2xl font-semibold text-[#073331]">{detailTitle}</h2>
              <p className="font-mono text-xs text-[#607C6E]">
                {normalizedSiteDraft.address || (language === 'sv' ? 'Osparat BRF-utkast' : 'Unsaved BRF draft')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className="cmd-label rounded px-1.5 py-0.5"
              style={
                normalizedSiteDraft.active
                  ? { border: '1px solid rgba(27,127,77,0.35)', color: '#1B7F4D', background: 'rgba(27,127,77,0.10)' }
                  : { border: '1px solid rgba(185,126,31,0.35)', color: '#B97E1F', background: 'rgba(185,126,31,0.12)' }
              }
            >
              {normalizedSiteDraft.active ? (language === 'sv' ? 'AKTIV' : 'ACTIVE') : (language === 'sv' ? 'INAKTIV' : 'INACTIVE')}
            </span>
            <span className="cmd-label rounded px-1.5 py-0.5 text-[#607C6E]" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
              {normalizedSiteDraft.status}
            </span>
            <span className="cmd-label rounded px-1.5 py-0.5 text-[#607C6E]" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
              <span className="font-mono tabular-nums text-[#073331]">{blockDrafts.length}</span> {language === 'sv' ? 'BLOCK' : 'BLOCKS'}
            </span>
            <span className="cmd-label rounded px-1.5 py-0.5 text-[#607C6E]" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
              <span className="font-mono tabular-nums text-[#073331]">{selectedSiteMonthJobs.length}</span> {language === 'sv' ? 'BESÖK' : 'VISITS'}
            </span>
          </div>
        </div>

        {detailFlags.length > 0 ? (
          <div
            className="flex flex-wrap items-center gap-2 rounded px-3 py-2"
            style={{
              border: '1px solid rgba(185,126,31,0.35)',
              background: 'rgba(185,126,31,0.06)',
            }}
          >
            <span className="cmd-label text-[#B97E1F]">AVVIKELSER</span>
            <div className="flex flex-wrap items-center gap-1">
              {detailFlags.map((flag) => {
                const meta = FLAG_META[flag];
                return (
                  <button
                    key={flag}
                    type="button"
                    onClick={() => {
                      if (flag === 'qc_failed_30d') {
                        window.location.hash = `#/qc/${selectedSite!.id}`;
                        return;
                      }
                      // Mirror flagFixRoute's same-site tab mapping.
                      const tab =
                        flag === 'missing_price'
                          ? 'pricing'
                          : flag === 'missing_fortnox'
                            ? 'general'
                            : flag === 'unassigned_7d'
                              ? 'jobs'
                              : 'blocks'; // missing_points, no_jobs_30d
                      navigateToRoute({ page: 'site', siteId: selectedSite!.id, tab });
                    }}
                    className="cmd-label px-1.5 py-0.5 rounded text-[8px] transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-current"
                    style={{ background: meta.bg, color: meta.color }}
                    title={meta.description}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-5 border-b border-[#073331]/10">
          {([
            { tab: 'general', label: { sv: 'ALLMÄNT', en: 'GENERAL' }, disabled: false },
            { tab: 'blocks', label: { sv: 'BLOCK', en: 'BLOCKS' }, disabled: false },
            { tab: 'extras', label: { sv: 'EXTRA', en: 'EXTRA BLOCKS' }, disabled: false },
            { tab: 'calendar', label: { sv: 'KALENDER', en: 'CALENDAR' }, disabled: isNewSite },
            { tab: 'jobs', label: { sv: 'JOBB', en: 'JOBS' }, disabled: isNewSite },
            { tab: 'pricing', label: { sv: 'PRISSÄTTNING', en: 'PRICING' }, disabled: isNewSite },
            { tab: 'print', label: { sv: 'UTSKRIFT', en: 'PRINT' }, disabled: isNewSite },
          ] as const).map(({ tab, label, disabled }) => {
            const active = route.tab === tab;
            return (
              <button
                key={tab}
                type="button"
                disabled={disabled}
                onClick={() => navigateToRoute({ page: 'site', siteId: routeSiteId ?? NEW_SITE_ID, tab })}
                className="cmd-label pb-2 -mb-px transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                style={{
                  color: active ? '#8A6A47' : '#607C6E',
                  borderBottom: active ? '1.5px solid #8A6A47' : '1.5px solid transparent',
                }}
              >
                {label[language]}
              </button>
            );
          })}
        </div>

        {isReadonlyTab ? (
          <div className="cmd-panel cmd-panel-inner rounded p-4 font-mono text-xs text-[#607C6E]">
            {language === 'sv' ? 'Spara BRF:en först innan du använder Kalender, Jobb eller Utskrift.' : 'Save the BRF first before using Calendar, Jobs, or Print.'}
          </div>
        ) : null}

        {route.tab === 'general' ? (
          <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="cmd-label">{language === 'sv' ? 'ALLMÄNT' : 'GENERAL'}</div>
                <p className="font-mono text-[11px] text-[#607C6E]">{language === 'sv' ? 'Redigera BRF-metadata och driftsnoteringar.' : 'Edit BRF metadata and operational notes.'}</p>
              </div>
              <Button
                type="button"
                onClick={() => void submitGeneralSave()}
                disabled={!canSaveGeneral}
                className="bg-[#073331] text-[#F0EEE6] hover:bg-[#0B423E]"
              >
                {isSavingSite ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {language === 'sv' ? 'Spara allmänt' : 'Save general'}
              </Button>
            </div>
            <div className="space-y-4">
              <InlineNotice notice={notice} />

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'BRF-namn' : 'BRF name'}</Label>
                  <Input value={siteDraft.name} onChange={(event) => updateSiteDraft({ name: event.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Kundnummer' : 'Customer number'}</Label>
                  <Input value={siteDraft.customer_number} onChange={(event) => updateSiteDraft({ customer_number: event.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Adress' : 'Address'}</Label>
                  <Input value={siteDraft.address} onChange={(event) => updateSiteDraft({ address: event.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Postnummer' : 'Postal code'}</Label>
                  <Input value={siteDraft.postal_code} onChange={(event) => updateSiteDraft({ postal_code: event.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Startdatum' : 'Service start date'}</Label>
                  <Input
                    type="date"
                    value={siteDraft.service_start_date}
                    onChange={(event) => updateSiteDraft({ service_start_date: event.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Status' : 'Status'}</Label>
                  <Select value={siteDraft.status} onValueChange={(value) => updateSiteDraft({ status: value as Site['status'] })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">active</SelectItem>
                      <SelectItem value="paused">paused</SelectItem>
                      <SelectItem value="archived">archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Byggnader' : 'Buildings'}</Label>
                  <Input value={siteDraft.building_count} onChange={(event) => updateSiteDraft({ building_count: event.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Våningar' : 'Floors'}</Label>
                  <Input value={siteDraft.floor_count} onChange={(event) => updateSiteDraft({ floor_count: event.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Lägenheter' : 'Apartments'}</Label>
                  <Input value={siteDraft.apartment_count} onChange={(event) => updateSiteDraft({ apartment_count: event.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Antal nycklar' : 'Key count'}</Label>
                  <Input value={siteDraft.key_count} onChange={(event) => updateSiteDraft({ key_count: event.target.value })} />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Nyckelinfo' : 'Key info'}</Label>
                  <Textarea
                    value={siteDraft.key_info}
                    onChange={(event) => updateSiteDraft({ key_info: event.target.value })}
                    className="min-h-[120px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Portkod' : 'Pin code'}</Label>
                  <Textarea
                    value={siteDraft.pin_code}
                    onChange={(event) => updateSiteDraft({ pin_code: event.target.value })}
                    className="min-h-[120px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Städrumsinstruktioner' : 'Cleaning room instructions'}</Label>
                  <Textarea
                    value={siteDraft.cleaning_room_instructions}
                    onChange={(event) => updateSiteDraft({ cleaning_room_instructions: event.target.value })}
                    className="min-h-[140px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="cmd-label">{language === 'sv' ? 'Interna noteringar' : 'Internal notes'}</Label>
                  <Textarea
                    value={siteDraft.internal_notes}
                    onChange={(event) => updateSiteDraft({ internal_notes: event.target.value })}
                    className="min-h-[140px]"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 rounded border border-[#073331]/12 px-3 py-2 text-sm text-[#2E4640]">
                <Checkbox
                  checked={siteDraft.active}
                  onCheckedChange={(checked) => updateSiteDraft({ active: Boolean(checked) })}
                />
                {language === 'sv' ? 'Aktiv' : 'Active'}
              </label>
            </div>
          </div>
        ) : null}

        {(route.tab === 'blocks' || route.tab === 'extras') ? (
          <>
            <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="cmd-label">{route.tab === 'extras' ? (language === 'sv' ? 'EXTRA BLOCK' : 'EXTRA BLOCKS') : (language === 'sv' ? 'BLOCK' : 'BLOCKS')}</div>
                  <p className="font-mono text-[11px] text-[#607C6E]">{route.tab === 'extras' ? 'Manage extra and one-off cleanings for this BRF.' : 'CRUD the live scheduling model for this BRF. Preview runs locally on the current draft.'}</p>
                </div>
                <div className="flex flex-col items-start gap-3">
                  <MonthScopeControls
                    language={language}
                    month={month}
                    year={year}
                    onMonthChange={setMonth}
                    onYearChange={setYear}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)} disabled={blockDrafts.length === 0}>
                      <Eye className="h-4 w-4" />
                      Preview month
                    </Button>
                    <Button
                      type="button"
                      onClick={() => void submitBlocksSave()}
                      disabled={!canSaveBlocks}
                      className="bg-[#073331] text-[#F0EEE6] hover:bg-[#0B423E]"
                    >
                      {isSavingBlocks ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Save blocks
                    </Button>
                    {role === 'admin' && selectedSite && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="cmd-label text-[#607C6E] hover:text-[#073331]"
                        onClick={() => void submitRegenerateManually()}
                      >
                        Regenerera manuellt
                      </Button>
                    )}
                    <span className="font-mono text-[11px] text-[#607C6E]">
                      {language === 'sv' ? 'Auto-uppdateras vid ändring' : 'Auto-updates on save'}
                      {(() => {
                        const latest = selectedSiteRuns
                          .filter((r) => r.finished_at)
                          .sort((a, b) => (b.finished_at ?? '').localeCompare(a.finished_at ?? ''))[0];
                        return latest?.finished_at
                          ? ` · senast ${formatRelative(latest.finished_at)}`
                          : '';
                      })()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <InlineNotice notice={notice} />

                {(siteDirty || blocksDirty) ? (
                  <div
                    className="rounded px-3 py-2 font-mono text-xs text-[#B97E1F]"
                    style={{ border: '1px solid rgba(185,126,31,0.35)', background: 'rgba(185,126,31,0.06)' }}
                  >
                    You have unsaved draft changes. Preview uses the draft. Save to publish.
                  </div>
                ) : null}

                <div className="grid gap-4 xl:grid-cols-[300px_minmax(0,1fr)]">
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {route.tab === 'extras' ? (
                        <>
                          <Button type="button" variant="outline" size="sm" onClick={createExtraBlock}>
                            <Plus className="h-4 w-4" />
                            New extra
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={createOneOffBlock}>
                            <Plus className="h-4 w-4" />
                            New one-off
                          </Button>
                        </>
                      ) : (
                        <Button type="button" variant="outline" size="sm" onClick={createBlock}>
                          <Plus className="h-4 w-4" />
                          New block
                        </Button>
                      )}
                      <Button type="button" variant="outline" size="sm" onClick={duplicateBlock} disabled={!selectedBlock}>
                        <Copy className="h-4 w-4" />
                        Duplicate
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={deleteSelectedBlock} disabled={!selectedBlock}>
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {blockDrafts
                        .filter((block) =>
                          route.tab === 'extras'
                            ? block.model_type === 'EXTRA_CLEANING_TEMPLATE'
                            : block.model_type === 'RECURRING_BLOCK'
                        )
                        .map((block) => (
                        <button
                          key={block.id}
                          type="button"
                          onClick={() => setSelectedBlockId(block.id ?? null)}
                          className={cn(
                            'group w-full rounded border px-3 py-3 text-left transition-colors',
                            block.id === selectedBlockId
                              ? 'border-[#8A6A47] bg-[#8A6A47]/[0.06]'
                              : 'border-[#073331]/12 hover:border-[#8A6A47]/40 hover:bg-[#073331]/[0.04]',
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                              <div className="font-mono text-xs font-medium text-[#073331]">{block.source_code || 'Untitled block'}</div>
                              <div className="font-mono text-[11px] text-[#607C6E]">{block.area || 'No area set'}</div>
                              <div className="font-mono text-[11px] text-[#607C6E]">{block.label || 'No label set'}</div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              {route.tab === 'extras' ? (
                                <span
                                  className="cmd-label rounded px-1.5 py-0.5"
                                  style={block.cadence === 'ONE_OFF'
                                    ? { border: '1px solid rgba(7,51,49,0.12)', color: '#607C6E' }
                                    : { border: '1px solid rgba(185,126,31,0.35)', color: '#B97E1F', background: 'rgba(185,126,31,0.12)' }}
                                >
                                  {block.cadence === 'ONE_OFF' ? 'One-off' : 'Recurring'}
                                </span>
                              ) : (
                                <span className="cmd-label rounded px-1.5 py-0.5 text-[#607C6E]" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
                                  Recurring
                                </span>
                              )}
                              <span className="font-mono text-[10px] tabular-nums text-[#607C6E]">#{block.sort_order}</span>
                            </div>
                          </div>
                        </button>
                      ))}

                      {blockDrafts.filter((block) =>
                          route.tab === 'extras'
                            ? block.model_type === 'EXTRA_CLEANING_TEMPLATE'
                            : block.model_type === 'RECURRING_BLOCK'
                        ).length === 0 ? (
                        <div className="rounded border border-dashed border-[#073331]/20 p-4 font-mono text-xs text-[#607C6E]">
                          {route.tab === 'extras' ? 'No extra blocks yet. Create the first extra.' : 'No blocks yet. Create the first rule.'}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-4">
                    {!selectedBlock || (route.tab === 'blocks' && selectedBlock.model_type !== 'RECURRING_BLOCK') || (route.tab === 'extras' && selectedBlock.model_type !== 'EXTRA_CLEANING_TEMPLATE') ? (
                      <div className="rounded border border-dashed border-[#073331]/20 p-4 font-mono text-xs text-[#607C6E]">
                        {route.tab === 'extras' ? 'Select an extra block to edit, or create a new one.' : 'Select a block to edit, or create a new one.'}
                      </div>
                    ) : (
                      <>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="cmd-label">Source code</Label>
                            <Input
                              value={selectedBlock.source_code}
                              onChange={(event) => updateBlockDraft(selectedBlock.id ?? '', { source_code: event.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="cmd-label">Sort order</Label>
                            <Input
                              type="number"
                              value={selectedBlock.sort_order}
                              onChange={(event) => updateBlockDraft(selectedBlock.id ?? '', { sort_order: Number(event.target.value) })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="cmd-label">Area</Label>
                            <Input
                              value={selectedBlock.area}
                              onChange={(event) => updateBlockDraft(selectedBlock.id ?? '', { area: event.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="cmd-label">Label</Label>
                            <Input
                              value={selectedBlock.label}
                              onChange={(event) => updateBlockDraft(selectedBlock.id ?? '', { label: event.target.value })}
                            />
                          </div>
                        </div>

                        <Separator />

                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="cmd-label">Cadence</Label>
                            <Select
                              value={selectedBlock.cadence}
                              onValueChange={(value) => {
                                const updates: Partial<OpsRuleDraft> = {
                                  cadence: value as WorkBlock['cadence'],
                                  ...(value !== 'BIWEEKLY' ? { first_visit_of_month: false } : {}),
                                };
                                if (value === 'ONE_OFF') {
                                  updates.weekdays = [];
                                  updates.exact_months = [];
                                  updates.parity = null;
                                  updates.week_in_month = null;
                                }
                                updateBlockDraft(selectedBlock.id ?? '', updates);
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(route.tab === 'extras'
                                  ? ['EXACT_MONTHS', 'ONE_OFF'] as const
                                  : ['WEEKLY', 'BIWEEKLY', 'MONTHLY'] as const
                                ).map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {selectedBlock.cadence === 'ONE_OFF' && (
                            <div className="space-y-2">
                              <Label className="cmd-label">One-off date</Label>
                              <Input
                                type="date"
                                value={selectedBlock.one_off_date ?? ''}
                                onChange={(event) => updateBlockDraft(selectedBlock.id ?? '', { one_off_date: event.target.value || null })}
                              />
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label className="cmd-label">Required photos</Label>
                            <Input
                              type="number"
                              value={selectedBlock.required_photos}
                              onChange={(event) => updateBlockDraft(selectedBlock.id ?? '', { required_photos: Number(event.target.value) })}
                            />
                          </div>
                          {route.tab === 'extras' && (
                            <div className="space-y-2">
                              <Label className="cmd-label">Pris ex moms (SEK)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Lämna tomt för gratis"
                                value={(selectedBlock as { extra_price_ex_moms_sek?: number | null }).extra_price_ex_moms_sek ?? ''}
                                onChange={(event) => {
                                  const raw = event.target.value;
                                  const parsed = raw === '' ? null : Number.parseFloat(raw);
                                  updateBlockDraft(selectedBlock.id ?? '', {
                                    extra_price_ex_moms_sek: parsed == null || !Number.isFinite(parsed) ? null : parsed,
                                  } as never);
                                }}
                              />
                              <p className="font-mono text-[11px] text-[#607C6E]">
                                Lägger till raden i BRFens månadsfaktura när jobbet godkänns.
                              </p>
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label className="cmd-label">Assigned cleaner</Label>
                            <Select
                              value={selectedBlock.default_user_id ?? NONE}
                              onValueChange={(value) =>
                                updateBlockDraft(selectedBlock.id ?? '', {
                                  default_user_id: value === NONE ? null : value,
                                })}
                              disabled={!canAssignCleaners || isUsersLoading}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={isUsersLoading ? 'Loading cleaners…' : 'Choose cleaner'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>Unassigned</SelectItem>
                                {cleanerUsers.map((user) => (
                                  <SelectItem key={user.id} value={user.id}>
                                    {user.full_name ?? user.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 text-sm text-[#2E4640]">
                            <Checkbox
                              checked={selectedBlock.active}
                              onCheckedChange={(checked) => updateBlockDraft(selectedBlock.id ?? '', { active: Boolean(checked) })}
                            />
                            Active
                          </label>
                          {selectedBlock.cadence === 'BIWEEKLY' && (
                            <label className="flex items-center gap-2 text-sm text-[#2E4640]">
                              <Checkbox
                                checked={selectedBlock.first_visit_of_month}
                                onCheckedChange={(checked) =>
                                  updateBlockDraft(selectedBlock.id ?? '', { first_visit_of_month: Boolean(checked) })}
                              />
                              First visit of month
                            </label>
                          )}
                        </div>

                        <Separator />

                        {selectedBlock.cadence !== 'ONE_OFF' && (
                        <>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <Label className="cmd-label">Weekdays</Label>
                            <div className="flex gap-2">
                              <Button type="button" variant="ghost" size="sm" onClick={() => updateBlockDraft(selectedBlock.id ?? '', { weekdays: [...allWeekdayValues] })}>
                                All
                              </Button>
                              <Button type="button" variant="ghost" size="sm" onClick={() => updateBlockDraft(selectedBlock.id ?? '', { weekdays: [] })}>
                                Clear
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {weekdayOptions.map((option) => {
                              const checked = selectedBlock.weekdays.includes(option.value);
                              return (
                                <label key={option.value} className="flex items-center gap-2 rounded border border-[#073331]/12 px-3 py-2 text-sm text-[#2E4640]">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(value) => {
                                      const next = new Set<number>(selectedBlock.weekdays);
                                      if (value) next.add(option.value);
                                      else next.delete(option.value);
                                      updateBlockDraft(selectedBlock.id ?? '', { weekdays: Array.from(next).sort((a, b) => a - b) });
                                    }}
                                  />
                                  {option.short}
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <Separator />

                        {route.tab !== 'extras' && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <Label className="cmd-label">Active months</Label>
                            <div className="flex gap-2">
                              <Button type="button" variant="ghost" size="sm" onClick={() => updateBlockDraft(selectedBlock.id ?? '', { active_months: [...allMonthValues] })}>
                                All
                              </Button>
                              <Button type="button" variant="ghost" size="sm" onClick={() => updateBlockDraft(selectedBlock.id ?? '', { active_months: [] })}>
                                Clear
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                            {monthOptions.map((option) => {
                              const checked = selectedBlock.active_months.includes(option.value);
                              return (
                                <label key={option.value} className="flex items-center gap-2 rounded border border-[#073331]/12 px-3 py-2 text-sm text-[#2E4640]">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(value) => {
                                      const next = new Set<number>(selectedBlock.active_months);
                                      if (value) next.add(option.value);
                                      else next.delete(option.value);
                                      updateBlockDraft(selectedBlock.id ?? '', { active_months: Array.from(next).sort((a, b) => a - b) });
                                    }}
                                  />
                                  {formatMonthLabel(option.value, language).slice(0, 3)}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        )}

                        {route.tab !== 'extras' && <Separator />}

                        {route.tab !== 'extras' && (
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="cmd-label">Parity</Label>
                            <Select
                              value={selectedBlock.parity ?? NONE}
                              onValueChange={(value) =>
                                updateBlockDraft(selectedBlock.id ?? '', {
                                  parity: value === NONE ? null : (value as WorkBlock['parity']),
                                })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>None</SelectItem>
                                {parityOptions.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="cmd-label">Week in month</Label>
                            <Select
                              value={selectedBlock.week_in_month ?? NONE}
                              onValueChange={(value) =>
                                updateBlockDraft(selectedBlock.id ?? '', {
                                  week_in_month: value === NONE ? null : (value as WorkBlock['week_in_month']),
                                })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>None</SelectItem>
                                {weekInMonthOptions.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        )}

                        {route.tab === 'extras' && selectedBlock.cadence === 'EXACT_MONTHS' && (
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="cmd-label">Week in month</Label>
                            <Select
                              value={selectedBlock.week_in_month ?? NONE}
                              onValueChange={(value) =>
                                updateBlockDraft(selectedBlock.id ?? '', {
                                  week_in_month: value === NONE ? null : (value as WorkBlock['week_in_month']),
                                })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>None</SelectItem>
                                {weekInMonthOptions.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        )}

                        {route.tab === 'extras' && selectedBlock.cadence === 'EXACT_MONTHS' && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <Label className="cmd-label">Exact months</Label>
                            <div className="flex gap-2">
                              <Button type="button" variant="ghost" size="sm" onClick={() => updateBlockDraft(selectedBlock.id ?? '', { exact_months: [...allMonthValues] })}>
                                All
                              </Button>
                              <Button type="button" variant="ghost" size="sm" onClick={() => updateBlockDraft(selectedBlock.id ?? '', { exact_months: [] })}>
                                Clear
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                            {monthOptions.map((option) => {
                              const checked = selectedBlock.exact_months.includes(option.value);
                              return (
                                <label key={option.value} className="flex items-center gap-2 rounded border border-[#073331]/12 px-3 py-2 text-sm text-[#2E4640]">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(value) => {
                                      const next = new Set<number>(selectedBlock.exact_months);
                                      if (value) next.add(option.value);
                                      else next.delete(option.value);
                                      updateBlockDraft(selectedBlock.id ?? '', { exact_months: Array.from(next).sort((a, b) => a - b) });
                                    }}
                                  />
                                  {formatMonthLabel(option.value, language).slice(0, 3)}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        )}
                        </>

                        )}

                        <Separator />

                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="cmd-label">Tasks</Label>
                            <span className="cmd-label text-[#607C6E]">
                              Total: <span className="font-mono tabular-nums text-[#073331]">{(selectedBlock.structured_tasks ?? []).reduce((s, t) => s + t.points, 0).toFixed(2)}</span> pts
                            </span>
                          </div>
                          <div className="space-y-1">
                            {(selectedBlock.structured_tasks ?? []).map((task, idx) => (
                              <div key={task.id} className="flex items-center gap-2">
                                <Input
                                  value={task.label}
                                  onChange={(e) => {
                                    const next = [...(selectedBlock.structured_tasks ?? [])];
                                    next[idx] = { ...next[idx], label: e.target.value };
                                    updateBlockDraft(selectedBlock.id ?? '', {
                                      structured_tasks: next,
                                    });
                                  }}
                                  className="flex-1"
                                  placeholder="Task label"
                                />
                                <Input
                                  type="number"
                                  value={task.points || ''}
                                  onChange={(e) => {
                                    const next = [...(selectedBlock.structured_tasks ?? [])];
                                    next[idx] = { ...next[idx], points: parseFloat(e.target.value) || 0 };
                                    updateBlockDraft(selectedBlock.id ?? '', {
                                      structured_tasks: next,
                                      points: Math.round(next.reduce((s, t) => s + t.points, 0) * 100) / 100,
                                    });
                                  }}
                                  className="w-20"
                                  step="0.25"
                                  min={0}
                                  placeholder="Pts"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const next = (selectedBlock.structured_tasks ?? []).filter((_, i) => i !== idx);
                                    updateBlockDraft(selectedBlock.id ?? '', {
                                      structured_tasks: next,
                                      points: Math.round(next.reduce((s, t) => s + t.points, 0) * 100) / 100,
                                    });
                                  }}
                                  className="h-8 w-8 p-0 text-destructive"
                                >
                                  x
                                </Button>
                              </div>
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const next = [
                                  ...(selectedBlock.structured_tasks ?? []),
                                  { id: createDraftId('task'), label: '', points: 0 },
                                ];
                                updateBlockDraft(selectedBlock.id ?? '', {
                                  structured_tasks: next,
                                  points: Math.round(next.reduce((s, t) => s + t.points, 0) * 100) / 100,
                                });
                              }}
                              className="w-full"
                            >
                              + Add task
                            </Button>
                          </div>
                        </div>

                        <div className="rounded border border-[#073331]/12 bg-[#073331]/[0.03] p-3">
                          <p className="cmd-label">Current schedule</p>
                          <p className="mt-1 font-mono text-[11px] text-[#607C6E]">{formatSchedule(selectedBlock, language)}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
              <DialogContent className="max-w-5xl bg-white">
                <DialogHeader>
                  <DialogTitle className="text-[#073331]">Preview {formatMonthLabel(month, language)} {year}</DialogTitle>
                  <DialogDescription className="font-mono text-xs text-[#607C6E]">
                    This preview runs locally on the current draft, including unsaved block edits.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  {previewState.error ? (
                    <div className="rounded border border-[#C24138]/30 bg-[#C24138]/[0.06] px-3 py-2 font-mono text-xs text-[#C24138]">
                      {previewState.error}
                    </div>
                  ) : null}

                  {previewState.summary ? (
                    <>
                      <div className="flex flex-wrap gap-6">
                        <div>
                          <div className="cmd-label">Jobs</div>
                          <div className="font-mono text-lg font-semibold tabular-nums text-[#073331]">{previewState.summary.total}</div>
                        </div>
                        <div>
                          <div className="cmd-label">Recurring</div>
                          <div className="font-mono text-lg font-semibold tabular-nums text-[#073331]">{previewState.summary.recurring}</div>
                        </div>
                        <div>
                          <div className="cmd-label">Extra</div>
                          <div className="font-mono text-lg font-semibold tabular-nums text-[#073331]">{previewState.summary.extra}</div>
                        </div>
                      </div>

                      <div className="overflow-x-auto cmd-scroll">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr>
                              <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10">Date</th>
                              <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Code</th>
                              <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Area</th>
                              <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Label</th>
                              <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Kind</th>
                              <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">ISO</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewState.summary.jobs.map((job) => (
                              <tr key={job.job_key} className="border-b border-[#073331]/10 hover:bg-[#073331]/[0.04] group">
                                <td className="py-1.5 pr-2 font-mono tabular-nums text-xs text-[#073331]">{job.scheduled_for}</td>
                                <td className="py-1.5 px-2 font-mono text-xs text-[#2E4640] group-hover:text-[#073331]">{job.block_source_code}</td>
                                <td className="py-1.5 px-2 font-mono text-xs text-[#2E4640] group-hover:text-[#073331]">{job.block_area}</td>
                                <td className="py-1.5 px-2 font-mono text-xs text-[#2E4640] group-hover:text-[#073331]">{job.block_label}</td>
                                <td className="py-1.5 px-2 font-mono text-xs text-[#607C6E]">{job.job_kind}</td>
                                <td className="py-1.5 px-2 font-mono tabular-nums text-xs text-[#607C6E]">{job.iso_weekday}/{job.iso_week}</td>
                              </tr>
                            ))}

                            {previewState.summary.jobs.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="text-center font-mono text-xs text-[#607C6E] py-4">
                                  No jobs would be generated for this month.
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : null}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : null}

        {route.tab === 'calendar' ? (
          <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-0.5">
                <div className="cmd-label">{language === 'sv' ? 'KALENDER' : 'CALENDAR'}</div>
                <p className="font-mono text-[11px] text-[#607C6E]">Visual month view of generated jobs for this BRF.</p>
              </div>
              <MonthScopeControls
                language={language}
                month={month}
                year={year}
                onMonthChange={setMonth}
                onYearChange={setYear}
                onRefresh={() => void refreshLiveData()}
                isRefreshing={isRefreshing}
                syncedAt={data?.syncedAt ?? null}
              />
            </div>
            <div className="space-y-4">
              <InlineNotice notice={notice} />
              <MonthGridCalendar
                language={language}
                year={year}
                month={month}
                itemsByDate={selectedSiteCalendarItems}
                showSiteName={false}
              />
              {selectedSiteMonthJobs.length === 0 ? (
                <div className="rounded border border-dashed border-[#073331]/20 p-4 font-mono text-xs text-[#607C6E]">
                  No generated jobs for this BRF in {formatMonthLabel(month, language)} {year}.
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {route.tab === 'jobs' ? (
          <div className="space-y-4">
            <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="cmd-label">{language === 'sv' ? 'JOBB' : 'JOBS'}</div>
                  <p className="font-mono text-[11px] text-[#607C6E]">Visit-level jobs for this BRF, with child blocks, assignment, completion, and approval state.</p>
                </div>
                <MonthScopeControls
                  language={language}
                  month={month}
                  year={year}
                  onMonthChange={setMonth}
                  onYearChange={setYear}
                  onRefresh={() => void refreshLiveData()}
                  isRefreshing={isRefreshing}
                  syncedAt={data?.syncedAt ?? null}
                />
              </div>
              <div className="space-y-4">
                <InlineNotice notice={notice} />

                <div className="flex flex-wrap gap-6">
                  <div>
                    <div className="cmd-label">Visits</div>
                    <div className="font-mono text-lg font-semibold tabular-nums text-[#073331]">{selectedSiteMonthJobs.length}</div>
                  </div>
                  <div>
                    <div className="cmd-label">{language === 'sv' ? 'Körningar' : 'Runs'}</div>
                    <div className="font-mono text-lg font-semibold tabular-nums text-[#073331]">{selectedSiteRuns.length}</div>
                  </div>
                  <div>
                    <div className="cmd-label">Approved</div>
                    <div className="font-mono text-lg font-semibold tabular-nums text-[#073331]">
                      {selectedSiteMonthJobs.filter((row) => row.status === 'approved').length}
                    </div>
                  </div>
                  <div>
                    <div className="cmd-label">Completed</div>
                    <div className="font-mono text-lg font-semibold tabular-nums text-[#073331]">
                      {selectedSiteMonthJobs.filter((row) => row.status === 'completed').length}
                    </div>
                  </div>
                  <div>
                    <div className="cmd-label">Assigned</div>
                    <div className="font-mono text-lg font-semibold tabular-nums text-[#073331]">
                      {selectedSiteMonthJobs.filter((row) => row.status === 'assigned').length}
                    </div>
                  </div>
                </div>

                {selectedSiteRuns.length > 0 ? (
                  <div className="rounded border border-[#073331]/12 bg-[#073331]/[0.03] p-3">
                    <div className="cmd-label mb-3">{language === 'sv' ? 'Schemahistorik' : 'Schedule history'}</div>
                    <div className="space-y-2 text-sm">
                      {selectedSiteRuns.map((run) => (
                        <div key={run.id} className="flex flex-wrap items-center gap-3">
                          <span className="cmd-label rounded px-1.5 py-0.5 text-[#607C6E] font-mono tabular-nums" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>{formatMonthLabel(run.target_month, language)} {run.target_year}</span>
                          <span
                            className="cmd-label rounded px-1.5 py-0.5"
                            style={run.status === 'completed'
                              ? { border: '1px solid rgba(27,127,77,0.35)', color: '#1B7F4D', background: 'rgba(27,127,77,0.10)' }
                              : { border: '1px solid rgba(185,126,31,0.35)', color: '#B97E1F', background: 'rgba(185,126,31,0.12)' }}
                          >
                            {run.status}
                          </span>
                          <span className="font-mono text-[11px] tabular-nums text-[#607C6E]">
                            {run.started_at.slice(0, 16).replace('T', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="cmd-panel cmd-panel-inner rounded p-4">
              <div className="overflow-x-auto cmd-scroll">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10">Date</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Blocks</th>
                      <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10">Pts</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Status</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Assigned</th>
                      <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSiteMonthJobs.map((row) => (
                      <tr key={row.id} className="border-b border-[#073331]/10 hover:bg-[#073331]/[0.04] group align-top">
                        <td className="py-1.5 pr-2 font-mono tabular-nums text-xs text-[#073331]">{row.scheduledFor}</td>
                        <td className="py-1.5 px-2">
                          <div className="space-y-0.5">
                            <div className="font-mono tabular-nums text-xs text-[#2E4640] group-hover:text-[#073331]">{row.blocks.length} blocks</div>
                            <div className="font-mono text-[10px] text-[#607C6E]">
                              {row.blocks.map((block) => block.sourceCode).join(', ')}
                            </div>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{row.totalPoints.toFixed(2)}</td>
                        <td className="py-1.5 px-2">
                          <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
                        </td>
                        <td className="py-1.5 px-2">
                          {(() => {
                            const reassignLocked =
                              row.status === 'approved' || row.status === 'completed';
                            const displayName = row.assignedUserId
                              ? (usersById.get(row.assignedUserId)?.full_name
                                ?? usersById.get(row.assignedUserId)?.email
                                ?? row.assignedUserId)
                              : '—';

                            if (!canAssignCleaners || reassignLocked) {
                              return <span className="font-mono text-xs text-[#2E4640] group-hover:text-[#073331]">{displayName}</span>;
                            }

                            return (
                              <Select
                                value={row.assignedUserId ?? NONE}
                                onValueChange={(value) =>
                                  void submitReassignJob(row.id, value === NONE ? null : value)
                                }
                                disabled={reassigningJobId === row.id || isUsersLoading}
                              >
                                <SelectTrigger className="w-[200px]">
                                  <SelectValue
                                    placeholder={isUsersLoading ? 'Laddar städare…' : 'Välj städare'}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={NONE}>Ej tilldelad</SelectItem>
                                  {cleanerUsers.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {getUserDisplayName(user)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          })()}
                        </td>
                        <td className="py-1.5 px-2">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedJobId(row.id)}
                            >
                              View
                            </Button>
                            {canApproveJobs ? (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => void submitApproveJob(row.id)}
                                disabled={!canApproveVisit(row) || approvingJobId === row.id}
                                className="bg-[#073331] text-[#F0EEE6] hover:bg-[#0B423E]"
                              >
                                {approvingJobId === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                Approve
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {selectedSiteMonthJobs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center font-mono text-xs text-[#607C6E] py-4">
                          No generated jobs for this month.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>

            <Dialog open={Boolean(selectedJob)} onOpenChange={(open) => !open && setSelectedJobId(null)}>
              <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden p-0 bg-white">
                <DialogHeader className="px-6 pt-6">
                  <DialogTitle className="text-[#073331]">{selectedJob?.siteName ?? 'Visit details'}</DialogTitle>
                  <DialogDescription className="font-mono text-xs text-[#607C6E]">
                    {selectedJob ? `${selectedJob.scheduledFor} · ${selectedJob.totalPoints.toFixed(2)} pts` : ''}
                  </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {selectedJob ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        <Badge variant={statusBadgeVariant(selectedJob.status)}>{selectedJob.status}</Badge>
                        <Badge variant="outline">
                          {selectedJob.assignedUserId
                            ? (usersById.get(selectedJob.assignedUserId)?.full_name ?? usersById.get(selectedJob.assignedUserId)?.email ?? selectedJob.assignedUserId)
                            : 'Unassigned'}
                        </Badge>
                      </div>

                      <div className="space-y-3">
                        {selectedJob.blocks.map((block) => (
                          <div key={block.id} className="rounded border border-[#073331]/12 p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="space-y-1">
                                <div className="font-mono text-sm font-medium text-[#073331]">{block.sourceCode} · {block.area}</div>
                                <div className="font-mono text-xs text-[#607C6E]">{block.label}</div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge variant={statusBadgeVariant(block.status)}>{block.status}</Badge>
                                <span className="cmd-label rounded px-1.5 py-0.5 text-[#607C6E]" style={{ border: '1px solid rgba(7,51,49,0.12)' }}><span className="font-mono tabular-nums text-[#073331]">{block.points.toFixed(2)}</span> pts</span>
                                <span className="cmd-label rounded px-1.5 py-0.5 text-[#607C6E]" style={{ border: '1px solid rgba(7,51,49,0.12)' }}><span className="font-mono tabular-nums text-[#073331]">{block.requiredPhotos}</span> photos</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <DialogFooter className="border-t border-[#073331]/10 bg-white px-6 py-4">
                  {selectedJob && canApproveJobs ? (
                    <Button
                      type="button"
                      onClick={() => void submitApproveJob(selectedJob.id)}
                      disabled={!canApproveVisit(selectedJob) || approvingJobId === selectedJob.id}
                      className="bg-[#073331] text-[#F0EEE6] hover:bg-[#0B423E]"
                    >
                      {approvingJobId === selectedJob.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Approve visit
                    </Button>
                  ) : null}
                  <Button type="button" variant="outline" onClick={() => setSelectedJobId(null)}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        ) : null}

        {route.tab === 'pricing' && routeSiteId && routeSiteId !== NEW_SITE_ID ? (
          <SitePricingPanel siteId={routeSiteId} />
        ) : null}

        {route.tab === 'print' ? (
          <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-4">
            <div className="no-print flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-0.5">
                <div className="cmd-label">{language === 'sv' ? 'UTSKRIFT' : 'PRINT'}</div>
                <p className="font-mono text-[11px] text-[#607C6E]">Print-friendly schedule rendering of generated jobs for this BRF.</p>
              </div>
              <div className="flex flex-col items-start gap-3">
                <MonthScopeControls
                  language={language}
                  month={month}
                  year={year}
                  onMonthChange={setMonth}
                  onYearChange={setYear}
                  onRefresh={() => void refreshLiveData()}
                  isRefreshing={isRefreshing}
                  syncedAt={data?.syncedAt ?? null}
                />
                <Button type="button" variant="outline" onClick={() => window.print()} disabled={selectedSiteMonthJobs.length === 0}>
                  <Printer className="h-4 w-4" />
                  Print schedule
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              <InlineNotice notice={notice} />

              {selectedSiteMonthJobs.length > 0 ? (
                <div id="schedule-print-content" className="rounded-md border bg-white p-6 text-slate-900">
                  <div className="mb-6 border-b border-slate-200 pb-4">
                    <div className="text-2xl font-semibold">{detailTitle}</div>
                    <div className="mt-1 text-sm text-slate-600">{normalizedSiteDraft.address}</div>
                    <div className="mt-3 text-sm text-slate-700">
                      {formatMonthLabel(month, language)} {year}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {Array.from(
                      selectedSiteMonthJobs.reduce((map, row) => {
                        const current = map.get(row.scheduledFor) ?? [];
                        current.push(row);
                        map.set(row.scheduledFor, current);
                        return map;
                      }, new Map<string, JobListRow[]>()),
                    ).map(([date, rows]) => (
                      <div key={date} className="space-y-2">
                        <div className="text-base font-semibold">{date}</div>
                        {rows.map((row) => (
                          <div key={row.id} className="mb-4 rounded-md border border-slate-200 p-4">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <div className="text-sm text-slate-700">
                                {row.assignedUserId
                                  ? `Assigned: ${usersById.get(row.assignedUserId)?.full_name ?? usersById.get(row.assignedUserId)?.email ?? row.assignedUserId}`
                                  : 'Unassigned'}
                              </div>
                              <div className="text-sm font-medium text-slate-800">{row.totalPoints.toFixed(2)} pts</div>
                            </div>
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className="border-b border-slate-200 text-left">
                                  <th className="py-2 pr-3 font-medium">Code</th>
                                  <th className="py-2 pr-3 font-medium">Area</th>
                                  <th className="py-2 pr-3 font-medium">Label</th>
                                  <th className="py-2 pr-3 font-medium">Points</th>
                                  <th className="py-2 font-medium">Photos</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.blocks.map((block) => (
                                  <tr key={block.id} className="border-b border-slate-100 align-top">
                                    <td className="py-2 pr-3">{block.sourceCode}</td>
                                    <td className="py-2 pr-3">{block.area}</td>
                                    <td className="py-2 pr-3">{block.label}</td>
                                    <td className="py-2 pr-3">{block.points.toFixed(2)}</td>
                                    <td className="py-2">{block.requiredPhotos}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded border border-dashed border-[#073331]/20 p-4 font-mono text-xs text-[#607C6E]">
                  No generated jobs to print for this month.
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-3 pb-4">
      {route.page !== 'site' ? <InlineNotice notice={notice} /> : null}

      {route.page === 'brfs' ? renderBrfList() : null}
      {route.page === 'calendar' ? renderGeneralCalendar() : null}
      {route.page === 'team' && canManageUsers ? renderTeamWorkspace() : null}
      {route.page === 'team-member' && canManageUsers ? renderTeamMemberDetail() : null}
      {route.page === 'site' ? renderSiteDetail() : null}
    </div>
  );
}

// ========================================================================
// Site Pricing panel (admin only) — Prissättning tab on BRF detail
// ========================================================================

const MONTHS_SV_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

function formatSek(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(value);
}

function SitePricingPanel({ siteId }: { siteId: string }) {
  const { session, profile } = useAuth();
  const token = session?.access_token ?? null;
  const isAdmin = profile?.role === 'admin';
  const [periods, setPeriods] = useState<SitePricingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SitePricingPeriod | { id?: null } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listSitePricing(siteId, token);
      setPeriods(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, siteId]);

  if (!isAdmin) {
    return (
      <div className="cmd-panel cmd-panel-inner rounded p-4 font-mono text-xs text-[#607C6E]">
        Prissättning är endast synlig för administratörer.
      </div>
    );
  }

  const handleSave = async (form: {
    id?: string | null;
    effectiveFrom: string;
    effectiveTo: string | null;
    monthMask: number[];
    priceExMomsSek: number;
    momsRate: number;
    note: string;
    fortnoxArticleNumber: string;
  }) => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await upsertSitePricing(
        {
          pricing: {
            id: form.id ?? undefined,
            siteId,
            effectiveFrom: form.effectiveFrom,
            effectiveTo: form.effectiveTo || null,
            monthMask: form.monthMask,
            priceExMomsSek: form.priceExMomsSek,
            momsRate: form.momsRate,
            fortnoxArticleNumber: form.fortnoxArticleNumber || null,
            note: form.note || null,
          },
        },
        token,
      );
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (!window.confirm('Ta bort prisperiod?')) return;
    try {
      await deleteSitePricing(id, token);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  return (
    <div className="cmd-panel cmd-panel-inner rounded p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="space-y-0.5">
          <div className="cmd-label">PRISSÄTTNING</div>
          <p className="font-mono text-[11px] text-[#607C6E]">Säsongspriser — varje rad = period + månader.</p>
        </div>
        <Button
          onClick={() => setEditing({ id: null })}
          disabled={saving}
          className="bg-[#073331] text-[#F0EEE6] hover:bg-[#0B423E]"
        >
          <Plus className="h-4 w-4 mr-1" />
          Ny prisperiod
        </Button>
      </div>
      <div>
        {error && (
          <div className="mb-3 px-3 py-2 rounded border border-[#C24138]/30 bg-[#C24138]/[0.06] font-mono text-xs text-[#C24138]">
            {error}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-[#607C6E]" />
          </div>
        ) : periods.length === 0 ? (
          <p className="font-mono text-xs text-[#607C6E] py-4">
            Inga prisperioder ännu. Klicka <em>Ny prisperiod</em> för att lägga till.
          </p>
        ) : (
          <div className="overflow-x-auto cmd-scroll">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10">Från</th>
                  <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Till</th>
                  <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Månader</th>
                  <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10">Pris ex moms</th>
                  <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10">Pris inc moms</th>
                  <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Artikel#</th>
                  <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">Notering</th>
                  <th className="border-b border-[#073331]/10 w-[100px]"></th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.id} className="border-b border-[#073331]/10 hover:bg-[#073331]/[0.04] group align-middle">
                    <td className="py-1.5 pr-2 font-mono tabular-nums text-xs text-[#073331]">{p.effective_from}</td>
                    <td className="py-1.5 px-2 font-mono tabular-nums text-xs text-[#607C6E]">{p.effective_to ?? '—'}</td>
                    <td className="py-1.5 px-2 font-mono text-xs text-[#2E4640] group-hover:text-[#073331]">
                      {p.month_mask.length === 12
                        ? 'Hela året'
                        : p.month_mask.slice().sort((a, b) => a - b).map((m) => MONTHS_SV_SHORT[m - 1]).join(', ')}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{formatSek(p.price_ex_moms_sek)}</td>
                    <td className="py-1.5 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{formatSek(p.price_inc_moms_sek)}</td>
                    <td className="py-1.5 px-2 font-mono text-xs text-[#607C6E]">{p.fortnox_article_number ?? '—'}</td>
                    <td className="py-1.5 px-2 font-mono text-xs text-[#607C6E] max-w-[240px] truncate" title={p.note ?? ''}>{p.note ?? '—'}</td>
                    <td className="py-1.5 px-2 text-right space-x-1 whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(p)} className="text-[#607C6E] hover:text-[#073331]">
                        Redigera
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-[#C24138] hover:text-[#C24138]">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && (
        <PricingEditorDialog
          initial={editing as SitePricingPeriod | { id?: null }}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}

function PricingEditorDialog({
  initial,
  onCancel,
  onSave,
  saving,
}: {
  initial: SitePricingPeriod | { id?: null };
  onCancel: () => void;
  onSave: (form: {
    id?: string | null;
    effectiveFrom: string;
    effectiveTo: string | null;
    monthMask: number[];
    priceExMomsSek: number;
    momsRate: number;
    note: string;
    fortnoxArticleNumber: string;
  }) => Promise<void>;
  saving: boolean;
}) {
  const existing = 'id' in initial && initial.id ? (initial as SitePricingPeriod) : null;
  const [effectiveFrom, setEffectiveFrom] = useState(existing?.effective_from ?? new Date().toISOString().slice(0, 10));
  const [effectiveTo, setEffectiveTo] = useState(existing?.effective_to ?? '');
  const [monthMask, setMonthMask] = useState<number[]>(existing?.month_mask ?? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  const [priceExMomsSek, setPriceExMomsSek] = useState(String(existing?.price_ex_moms_sek ?? ''));
  const [momsRate, setMomsRate] = useState(String(existing?.moms_rate ?? 0.25));
  const [note, setNote] = useState(existing?.note ?? '');
  const [fortnoxArticleNumber, setFortnoxArticleNumber] = useState(existing?.fortnox_article_number ?? '');

  const toggleMonth = (m: number) => {
    setMonthMask((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m].sort((a, b) => a - b)));
  };

  const priceIncPreview = useMemo(() => {
    const ex = parseFloat(priceExMomsSek);
    const rate = parseFloat(momsRate);
    if (!Number.isFinite(ex) || !Number.isFinite(rate)) return null;
    return Math.round(ex * (1 + rate) * 100) / 100;
  }, [priceExMomsSek, momsRate]);

  const handleSubmit = () => {
    const price = parseFloat(priceExMomsSek);
    const rate = parseFloat(momsRate);
    if (!Number.isFinite(price) || price < 0 || !Number.isFinite(rate) || rate < 0 || rate > 1) return;
    if (monthMask.length === 0) return;
    void onSave({
      id: existing?.id ?? null,
      effectiveFrom,
      effectiveTo: effectiveTo || null,
      monthMask,
      priceExMomsSek: price,
      momsRate: rate,
      note,
      fortnoxArticleNumber,
    });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#073331]">{existing ? 'Redigera prisperiod' : 'Ny prisperiod'}</DialogTitle>
          <DialogDescription className="font-mono text-xs text-[#607C6E]">Period gäller för de markerade månaderna inom datumintervallet.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="cmd-label">Från datum</Label>
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="cmd-label">Till datum (valfritt)</Label>
            <Input type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="cmd-label">Pris ex moms (SEK)</Label>
            <Input type="number" step="0.01" value={priceExMomsSek} onChange={(e) => setPriceExMomsSek(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="cmd-label">Moms (0-1)</Label>
            <Input type="number" step="0.01" value={momsRate} onChange={(e) => setMomsRate(e.target.value)} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="cmd-label">Månader</Label>
            <div className="flex flex-wrap gap-2 pt-1">
              {MONTHS_SV_SHORT.map((mLabel, idx) => {
                const m = idx + 1;
                const active = monthMask.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMonth(m)}
                    className="cmd-label rounded px-2.5 py-1 transition-colors"
                    style={active
                      ? { border: '1px solid #8A6A47', color: '#8A6A47', background: 'rgba(138,106,71,0.08)' }
                      : { border: '1px solid rgba(7,51,49,0.12)', color: '#607C6E' }}
                  >
                    {mLabel}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="cmd-label">Fortnox artikelnr (valfritt)</Label>
            <Input value={fortnoxArticleNumber} onChange={(e) => setFortnoxArticleNumber(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="cmd-label">Pris inc moms (förhandsvisning)</Label>
            <div className="pt-2 font-mono text-sm font-semibold tabular-nums text-[#073331]">{formatSek(priceIncPreview)}</div>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="cmd-label">Notering (valfritt)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>Avbryt</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#073331] text-[#F0EEE6] hover:bg-[#0B423E]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            <span className="ml-1">Spara</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========================================================================
// Cleaner Points panel — Poäng tab on team-member detail
// ========================================================================

export function CleanerPointsPanel({ userId }: { userId: string }) {
  const { session, profile } = useAuth();
  const token = session?.access_token ?? null;
  const isAdmin = profile?.role === 'admin';
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [totals, setTotals] = useState<{ points: number; pending: number; paid: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [paidFilter, setPaidFilter] = useState<'all' | 'true' | 'false'>('all');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listCleanerPoints(
        userId,
        { paid: paidFilter },
        token,
      );
      setEntries(res.entries);
      setTotals({
        points: res.totals.points,
        pending: res.totals.pending,
        paid: res.totals.paid,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId, paidFilter]);

  return (
    <div className="cmd-panel cmd-panel-inner rounded p-4 space-y-3">
      <div>
        <span className="cmd-label">POÄNG &amp; LÖN</span>
        <p className="mt-1 font-mono text-[11px] text-[#607C6E]">Ackumulerade poäng från godkända jobb.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="rounded px-3 py-2" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
          <div className="cmd-label">TOTALT</div>
          <div className="mt-1 font-mono tabular-nums text-lg font-semibold text-[#073331]">{totals?.points ?? 0} p</div>
        </div>
        <div className="rounded px-3 py-2" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
          <div className="cmd-label">OBETALT</div>
          <div className="mt-1 font-mono tabular-nums text-lg font-semibold text-[#B97E1F]">{totals?.pending ?? 0} p</div>
        </div>
        <div className="rounded px-3 py-2" style={{ border: '1px solid rgba(7,51,49,0.12)' }}>
          <div className="cmd-label">UTBETALT</div>
          <div className="mt-1 font-mono tabular-nums text-lg font-semibold text-[#1B7F4D]">{totals?.paid ?? 0} p</div>
        </div>
        <div className="ml-auto">
          <select
            value={paidFilter}
            onChange={(e) => setPaidFilter(e.target.value as 'all' | 'true' | 'false')}
            className="rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] focus:outline-none focus-visible:border-[#8A6A47]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          >
            <option value="all">ALLA</option>
            <option value="false">OBETALDA</option>
            <option value="true">UTBETALDA</option>
          </select>
        </div>
      </div>
      {error && (
        <div className="rounded p-2 font-mono text-xs text-[#C24138]" style={{ border: '1px solid rgba(194,65,56,0.3)', background: 'rgba(194,65,56,0.06)' }}>
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-[#607C6E]" />
        </div>
      ) : entries.length === 0 ? (
        <p className="font-mono text-xs text-[#607C6E] py-4">Inga poäng i det valda intervallet.</p>
      ) : (
        <div className="overflow-x-auto cmd-scroll">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10 w-28">GODKÄNT</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">TYP</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10">BRF</th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-24">POÄNG</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-[#073331]/10 align-middle" style={{ height: 34 }}>
                  <td className="py-1 pr-2 font-mono text-xs text-[#2E4640]">{e.approved_at.slice(0, 10)}</td>
                  <td className="py-1 px-2">
                    <span className="cmd-label" style={{ color: e.kind === 'extra' ? '#8A6A47' : e.kind === 'adjustment' ? '#B97E1F' : '#607C6E' }}>
                      {e.kind}
                    </span>
                  </td>
                  <td className="py-1 px-2 font-mono text-xs text-[#2E4640]">{e.site_name ?? '—'}</td>
                  <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">{e.points}</td>
                  <td className="py-1 px-2">
                    {e.paid_out_at ? (
                      <span
                        className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide"
                        style={{ background: 'rgba(27,127,77,0.10)', border: '1px solid rgba(27,127,77,0.35)', color: '#1B7F4D' }}
                      >
                        Utbetald
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide"
                        style={{ background: 'rgba(185,126,31,0.12)', border: '1px solid rgba(185,126,31,0.35)', color: '#B97E1F' }}
                      >
                        Väntar
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isAdmin && entries.some((e) => !e.paid_out_at) && (
        <p className="font-mono text-[11px] text-[#607C6E]">
          Admin: använd <code>/api/ops/cleaners/points/mark-paid</code> för att markera som utbetalda.
        </p>
      )}
    </div>
  );
}
